import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { helperService, IEventRegisterInput, IEventRegisterRow, nonLicensed } from "../../services/helper.service";
import { apiService } from "../../services";
import { MultiEntryMappingService } from "../../services/multi-entry-mapping.service";
import {
  ShowTypes,
  isCwagsFeoShowWide,
  isSameUkcRallyClass,
  isUkcRallyShow,
  parseUkcRallyClass,
  toUkcRallyFormat,
  ukcRallyClassLabel,
  ukcRallyShowFormat,
} from "@eagerdog/constants";

import {
  IDogClass,
  IShow,
  IShowElement,
  IDog,
  IEvent,
  IDogRegistrationInfo,
  IShowRunTimeSlot,
  IDogClubRegistration,
  IDogProposedChanges
} from "@eagerdog/interfaces";

import { Nosework } from "./forms/Nosework";
import { BarnHunt } from "./forms/BarnHunt";
import { SniffNGo } from "./forms/SniffNGo";
import { OnLeashRunThrough } from "./forms/OnLeashRunThrough";
import { GeneralDockDiving } from "./forms/GeneralDockDiving";
import { ObedienceShowNGo } from "./forms/ObedienceShowNGo";
import { GeneralRally } from "./forms/GeneralRally";
import { CWagsScent } from "./forms/CWagsScent";
import { CWagsObedience } from "./forms/CWagsObedience";
import { CWagsRally } from "./forms/CWagsRally";
import { CWagsGames } from "./forms/CWagsGames";
import { FHRallyObedience } from "./forms/FHRallyObedience";
import { FHAgility } from "./forms/FHAgility";
import { CDSPVideo } from "./forms/CDSPVideo";
import { Conformation } from "./forms/Conformation";
import { ConformationJunior } from "./forms/ConformationJunior";
import { Obedience } from "./forms/Obedience";
import { RallyObedience } from "./forms/RallyObedience";
import { UKCAgility } from "./forms/UKCAgility";
import { WeightPull } from "./forms/WeightPull";
import { UKCPrecisionCoursing } from "./forms/UKCPrecisionCoursing";
import { UKCShedDog } from "./forms/UKCShedDog";
import { DockJumping } from "./forms/DockJumping";

import { ABIConformation } from "./forms/ABIConformation";
import { ABIUrbanRatRace } from "./forms/ABIUrbanRatRace";
import { ABICountryRatRace } from "./forms/ABICountryRatRace";
import { ABIScentDogGames } from "./forms/ABIScentDogGames";
import { ABILostAndFound } from "./forms/ABILostAndFound";

import { ISWSConformation } from "./forms/ISWSConformation";
import { CKCConformation } from "./forms/CKCConformation";
import { CKCObedience } from "./forms/CKCObedience";
import { CKCRallyObedience } from "./forms/CKCRallyObedience";
import { CKCScentDetection } from "./forms/CKCScentDetection";

import { AKCConformation } from "./forms/AKCConformation";
import { AKCConformationJunior } from "./forms/AKCConformationJunior";

import { AKCObedience } from "./forms/AKCObedience";
import { AKCRallyObedience  } from "./forms/AKCRallyObedience";
import { AKCScentwork } from "./forms/AKCScentwork";
import { AKCLureCoursing } from "./forms/AKCLureCoursing";

import { AKCFastCat } from "./forms/AKCFastCat";
import { ISWSFastDash } from "./forms/ISWSFastDash";
import { AKCCAT } from "./forms/AKCCAT";
import { AKCFetch } from "./forms/AKCFetch";
import { AKCFarmDog } from "./forms/AKCFarmDog";
import { AKCAtt } from "./forms/AKCAtt";
import { AKCCanineGoodCitizen } from "./forms/AKCCanineGoodCitizen";
import { ASFAFieldTrial } from "./forms/ASFAFieldTrial";

import { AKCHerding } from "./forms/AKCHerding";
import { AHBAHerding } from "./forms/AHBAHerding";
import { AKCAgility } from "./forms/AKCAgility";
import { AKCTrickDog } from "./forms/AKCTrickDog";

import { CPESpeedway } from "./forms/CPESpeedway";
import { GCRNitrous50 } from "./forms/GCRNitrous50";
import { GCRNitrous100 } from "./forms/GCRNitrous100";

import { PWDCAWaterDog } from "./forms/PWDCAWaterDog";

import { IShowDogs } from "../../routes/EventRegister/EventRegister";
import Dropdown, { IOption } from "../../components/Dropdown/Dropdown";
import Checkbox from "../../components/Checkbox/Checkbox";
import Input from "../../components/Input/Input";
import { toast } from "../../components/Toast/ToastManager";
import MissingFieldsModal from "../MissingFieldsModal/MissingFieldsModal";
import { RegistrationContext, RegistrationField, resolveMissingFields } from "../../registration-requirements";

import TimeSlot from "./TimeSlot/TimeSlot";
import DogList from "../DogList";
import { ExtraTeammates } from "./ExtraTeammates";

import { nestedElementSetupShows } from "../StepForm/ShowForm/utils";

import './FormBuilder.scss';

interface IProps {
  classes: IDogClass[],
  dog: IDog | IDogRegistrationInfo,
  onChange(result: any): void,
  show: IShow,
  event?: IEvent,
  edit?: boolean,
  registration?: any,
  moveUp?: boolean,
  overrideDisable?: boolean,
  isJunior?: boolean,
  disabled?: boolean,
  isClubManager?: boolean, // Flag for on-behalf-of registrations (club managers OR owners with admin capacity)
  // NOTE: AKC Team purpose
  userDogs?: IDog[],
  showDogs?: IShowDogs,
  onAddDog?: () => void
  onEditDog?: (dog: IDog) => void
  onRefreshDogData?: (updatedDog?: IDog) => void // For validation errors - refresh dog data without selectDog
  refreshTimeslots?: number // Add a prop to trigger timeslot refresh
  onTimeslotsLoaded?: (showId: string, timeslots: IShowRunTimeSlot[]) => void // notify parent of loaded timeslot count
  waitlistedClasses?: Array<{show_element: string, level: string, section?: string}> // Waitlisted classes for blue checkmarks
}

export interface IDogClassElement extends IDogClass {
  id: string,
  start_minutes?: number,
  time_slot?: string,
  score_id?: string,
  // CAT-specific fields
  plan_to_earn_title?: string,
  title?: string,
  is_brachycephalic?: boolean,
  asfa_first_trial_for_hound?: boolean,
  asfa_first_time_entry?: boolean,
  asfa_info_changed_since_last_trial?: boolean,
  asfa_info_changed_regarding?: string,
  asfa_dismissed_last_6_trials?: boolean
}

export interface IEventRegisterInputElement extends IEventRegisterInput {
  id: string
}

interface IEventRegisterRowElement {
  allowDuplicates?: boolean,
  duplicate?: boolean,
  inputs: IEventRegisterInputElement[]
}

const FormBuilder: React.FC<IProps> = (rawProps) => {
  // UKC Rally: the form's options are the class labels ("RO1 A", "RO1 B", ...). The show and
  // the dog's classes are presented in that label form regardless of how the show stores them,
  // and the classes handed back to the parent are written in the show's own format
  // (level "RO1 B" for shows created before the refactor, level RO1 + section B otherwise).
  const isUkcRallyForm = isUkcRallyShow(rawProps.event?.sanctioning_club, rawProps.show?.show_type);
  const ukcRallyFormat = isUkcRallyForm ? ukcRallyShowFormat(rawProps.show?.show_elements) : "canonical";
  const rawOnChange = rawProps.onChange;
  const bridgedShow = useMemo<IShow>(() => {
    if (!isUkcRallyForm || !rawProps.show) return rawProps.show;
    return {
      ...rawProps.show,
      show_elements: (rawProps.show.show_elements || []).map((se: any) => toUkcRallyFormat(se, "legacy")),
    } as IShow;
  }, [isUkcRallyForm, rawProps.show]);
  const bridgedClasses = useMemo<IDogClass[]>(() => {
    if (!isUkcRallyForm) return rawProps.classes;
    return (rawProps.classes || []).map((c: any) => toUkcRallyFormat(c, "legacy"));
  }, [isUkcRallyForm, rawProps.classes]);
  const bridgedWaitlisted = useMemo(() => {
    if (!isUkcRallyForm || !rawProps.waitlistedClasses) return rawProps.waitlistedClasses;
    return rawProps.waitlistedClasses.map((wc: any) => toUkcRallyFormat(wc, "legacy"));
  }, [isUkcRallyForm, rawProps.waitlistedClasses]);
  const bridgedOnChange = useCallback((result: any) => {
    if (isUkcRallyForm && Array.isArray(result)) {
      rawOnChange(result.map((c: any) => toUkcRallyFormat(c, ukcRallyFormat)));
      return;
    }
    rawOnChange(result);
  }, [isUkcRallyForm, ukcRallyFormat, rawOnChange]);
  const props: IProps = isUkcRallyForm
    ? { ...rawProps, show: bridgedShow, classes: bridgedClasses, waitlistedClasses: bridgedWaitlisted, onChange: bridgedOnChange }
    : rawProps;

  const [classes, setClasses] = useState<IDogClassElement[]>([]);

  // C-WAGS Scent / Games: FEO (For Exhibition Only) rows sit under each regular row with the
  // same show_element; the fixed input.section ("FEO") tells the two apart.
  const isCwagsFeoShowWideShow = isCwagsFeoShowWide(props.event?.sanctioning_club, props.show?.show_type);
  const sectionKey = (section?: string) => (section && section !== "None" ? section : "");
  const sameSectionAsInput = (c: { section?: string }, input: { section?: string }) =>
    !isCwagsFeoShowWideShow || sectionKey(c.section) === sectionKey(input.section);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [isSyncingFromParent, setIsSyncingFromParent] = useState<boolean>(false);
  const [form, setForm] = useState<IEventRegisterRowElement[]>();

  const [timeslots, setTimeslots] = useState<IShowRunTimeSlot[]>([]);
  const [selectedTimeslot, setSelectedTimeslot] = useState<number>(-1);
  /** Per-class timeslot selection for OSNG / Rally Show N Go (level → slot index within that level's pool) */
  const [selectedTimeslotByLevel, setSelectedTimeslotByLevel] = useState<Record<string, number>>({});

  // Keep a ref to classes so async callbacks (e.g. refreshTimeslots) read the latest value
  // without causing the useEffect to re-run on every class change.
  const classesRef = useRef<IDogClassElement[]>(classes);
  useEffect(() => { classesRef.current = classes; }, [classes]);
  const dogRef = useRef(props.dog);
  useEffect(() => { dogRef.current = props.dog; }, [props.dog]);
  const pendingClassRetryRef = useRef<(() => void) | null>(null);
  const [missingFieldsRequest, setMissingFieldsRequest] = useState<{
    context: RegistrationContext;
    fields: RegistrationField[];
  } | null>(null);

  const [elementCount, setElementCount] = useState<number>();
  const [existingChanges, setExistingChanges] = useState<IDogProposedChanges>()

  const prevClasses = helperService.usePrevious(classes);
  const createId = () => {
    return Math.random().toString(36).substring(2, 8);
  }

  useEffect(() => {
    if (props.dog && "proposed_changes" in props.dog && props.dog.proposed_changes) {
      const found = props.dog.proposed_changes.find(pc => pc.event_id === props.event?._id);
      setExistingChanges(found);
    }
  }, [props.dog, props.event]);

  // Helper function to get club-specific registration info for the current event
  const getClubRegistrationInfo = useCallback((): IDogClubRegistration | null => {
    if (!props.event?.sanctioning_club) return null;

    if (existingChanges && existingChanges.club_registrations) {
      const clubReg = existingChanges.club_registrations.find(
        (reg: { old: IDogClubRegistration, new: IDogClubRegistration }) => reg.new?.sanctioning_club === props.event!.sanctioning_club
      )?.new;

      if (clubReg) return clubReg
    }
    // Check if dog has club_registrations array (new structure - IDog)
    if ('club_registrations' in props.dog && props.dog.club_registrations?.length) {
      const clubReg = props.dog.club_registrations.find(
        (reg: IDogClubRegistration) => reg.sanctioning_club === props.event!.sanctioning_club
      );
      return clubReg || null;
    }
    
    return null;
  }, [props.dog, props.event, existingChanges]);

  const yearList:any[] = [
    { level: Constants.dog_class_element_level.novice_puppy, min: 0, max: 6 },
    { level: Constants.dog_class_element_level.puppy, min: 6, max: 12 },
    { level: Constants.dog_class_element_level.junior, min: 12, max: 24 },
    { level: Constants.dog_class_element_level.adult, min: 24, max: -1 },
    { level: Constants.dog_class_element_level.open, min: 6, max: -1 },
    { level: Constants.dog_class_element_level.breader_handler, min: 6, max: -1 },
    { level: Constants.dog_class_element_level.champion, min: 6, max: -1 },
    { level: Constants.dog_class_element_level.grand_champion, min: 6, max: -1 },
    { level: Constants.dog_class_element_level.akc_4_6_month_puppy, min: 4, max: 6 },
    { level: Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only, min: 4, max: 6 },
    { level: Constants.dog_class_element_level.akc_6_9_month_puppy, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.akc_9_12_month_puppy, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.akc_12_18_month_puppy, min: 12, max: 18 },
    { level: Constants.dog_class_element_level.akc_18_24_month_puppy, min: 18, max: 24 },
    { level: Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor, min: 0, max: 12 },
    { level: Constants.dog_class_element_level.akc_adult_bred_by_exhibitor, min: 6, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_8_10_years, min: 96, max: 120 },
    { level: Constants.dog_class_element_level.akc_veteran_8_11_years, min: 96, max: 132 },
    { level: Constants.dog_class_element_level.akc_veteran_10_12_years, min: 120, max: 156 },
    { level: Constants.dog_class_element_level.akc_veteran_11_plus_years, min: 132, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_13_plus_years, min: 156, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_8_plus_years, min: 96, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_7_plus_years, min: 84, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_6_8_years, min: 72, max: 96 },
    { level: Constants.dog_class_element_level.akc_veteran_6_plus_years, min: 72, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_7_10_years, min: 84, max: 120 },
    { level: Constants.dog_class_element_level.akc_veteran_7_9_years, min: 84, max: 108 },
    { level: Constants.dog_class_element_level.akc_veteran_9_plus_years, min: 108, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_12_plus_years, min: 144, max: -1 },
    // Veteran Sweepstakes age validation
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10, min: 84, max: 120 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10, min: 96, max: 120 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11, min: 96, max: 132 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12, min: 120, max: 144 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus, min: 132, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13, min: 120, max: 156 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus, min: 144, max: -1 },
    { level: Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus, min: 156, max: -1 },
    // Puppy Sweepstakes age validation
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18, min: 12, max: 18 },
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15, min: 12, max: 15 },
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18, min: 15, max: 18 },
    { level: Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24, min: 18, max: 24 },
    // Dual-Title Sweepstakes age validation
    { level: Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y, min: 6, max: 84 },
    { level: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus, min: 84, max: -1 },
    { level: Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y, min: 84, max: 132 },
    { level: Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus, min: 132, max: -1 },
    
    // ISWS age validation - Puppy Classes
    { level: Constants.dog_class_element_level.isws_puppy_3_6, min: 3, max: 6 },
    { level: Constants.dog_class_element_level.isws_puppy_6_9, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.isws_puppy_6_12, min: 6, max: 12 },
    { level: Constants.dog_class_element_level.isws_puppy_9_12, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.isws_junior_puppy, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.isws_senior_puppy, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.isws_1218, min: 12, max: 18 },
    
    // ISWS age validation - Veteran Classes
    { level: Constants.dog_class_element_level.isws_veteran, min: 84, max: -1 }, // 7+ years
    { level: Constants.dog_class_element_level.isws_veteran_7_10, min: 84, max: 120 },
    { level: Constants.dog_class_element_level.isws_veteran_10_plus, min: 120, max: -1 },
    { level: Constants.dog_class_element_level.isws_veteran_10_13, min: 120, max: 156 },
    { level: Constants.dog_class_element_level.isws_veteran_13_plus, min: 156, max: -1 },
    
    // ISWS age validation - Sweepstakes Puppy
    { level: Constants.dog_class_element_level.isws_sweeps_puppy_3_6, min: 3, max: 6 },
    { level: Constants.dog_class_element_level.isws_sweeps_puppy_6_12, min: 6, max: 12 },
    { level: Constants.dog_class_element_level.isws_sweeps_12_24, min: 12, max: 24 },
    
    // ISWS age validation - Sweepstakes Veteran
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_7_plus, min: 84, max: -1 },
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_7_10, min: 84, max: 120 },
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_10_plus, min: 120, max: -1 },
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_7_9, min: 84, max: 108 },
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_9_13, min: 108, max: 156 },
    { level: Constants.dog_class_element_level.isws_sweeps_veteran_13_plus, min: 156, max: -1 },

    // CKC age validation (CKC Conformation Show Rules 12.2.1, 12.6.1, 12.8.3) - measured on show date
    { level: Constants.dog_class_element_level.ckc_junior_puppy, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.ckc_senior_puppy, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.ckc_12_18_month, min: 12, max: 18 },
    { level: Constants.dog_class_element_level.ckc_baby_puppy, min: 4, max: 6 },
    { level: Constants.dog_class_element_level.ckc_veterans, min: 84, max: -1 },
    { level: Constants.dog_class_element_level.ckc_sweepstakes_4_6, min: 4, max: 6 },
    { level: Constants.dog_class_element_level.ckc_sweepstakes_6_9, min: 6, max: 9 },
    { level: Constants.dog_class_element_level.ckc_sweepstakes_9_12, min: 9, max: 12 },
    { level: Constants.dog_class_element_level.ckc_sweepstakes_12_18, min: 12, max: 18 },
    { level: Constants.dog_class_element_level.ckc_veterans_sweepstakes, min: 84, max: -1 },
    // Altered: spayed or neutered dogs over 6 months (13.4.1)
    { level: Constants.dog_class_element_level.ckc_altered, min: 6, max: -1 },
  ];

  const createForm = useCallback((_classes: IDogClassElement[]) => {
    let template:IEventRegisterRow[] = Nosework.form;


    if (props.event) {
      if (props.event.sanctioning_club === Constants.sanctioning_club.UKC) {
        switch (props.show.show_type) {
          case Constants.dog_class_element.conformation:
            if (props.isJunior) {
              template = ConformationJunior.getForm(props.show);
            } else {
              template = Conformation.getForm(props.show);
            }
          break;
          case Constants.dog_class_element.performance_obedience:
            template = Obedience.form;
          break;
          case Constants.dog_class_element.performance_rally_obedience:
            template = RallyObedience.form;
          break;
          case Constants.dog_class_element.performance_agility:
            template = UKCAgility.getForm(props.show);
          break;
          case Constants.dog_class_element.performance_weight_pull:
            template = WeightPull.getForm(props.show);
          break;
          case Constants.dog_class_element.precision_coursing:
            template = UKCPrecisionCoursing.getForm(props.show);
          break;
          case Constants.show_type.shed_dog:
            template = UKCShedDog.form;
          break;
          case Constants.show_type.dock_jumping:
            template = DockJumping.getForm(props.show);
          break;
          default:
            template = Nosework.form;
          break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.ABI) {
        switch (props.show.show_type) {
          case Constants.dog_class_element.conformation:
            template = ABIConformation.getForm(props.show);
          break;
          case Constants.dog_class_element.performance_urban_rat_race:
            template = ABIUrbanRatRace.form;
          break;
          case Constants.show_type.country_rat_race:
            template = ABICountryRatRace.form;
          break;
          case Constants.show_type.scent_dog_games:
            template = ABIScentDogGames.getForm(props.show);
          break;
          case Constants.show_type.lost_and_found:
            template = ABILostAndFound.form;
          break;
          default:
            template = ABIConformation.getForm(props.show);
          break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.ISWS) {
        switch (props.show.show_type) {
          case Constants.dog_class_element.conformation:
            template = ISWSConformation.getForm(props.show, props.isJunior);
          break;
          default:
            template = ISWSConformation.getForm(props.show, props.isJunior);
          break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.CKC) {
        switch (props.show.show_type) {
          case Constants.show_type.obedience:
            template = CKCObedience.getForm(props.show);
          break;
          case Constants.show_type.rally_obedience:
            template = CKCRallyObedience.getForm(props.show);
          break;
          case Constants.show_type.scent_detection:
            template = CKCScentDetection.getForm(props.show);
          break;
          case Constants.show_type.conformation:
          default:
            template = CKCConformation.getForm(props.show);
          break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.AKC) {
        switch (props.show.show_type) {
          case Constants.show_type.lure_coursing:
            template = AKCLureCoursing.form;
          break;
          case Constants.show_type.fast_cat:
          case Constants.show_type.fast_dash:
          case Constants.show_type.cat:
          case Constants.show_type.fetch:

            if (props.show.show_type === Constants.show_type.fast_cat) {
              template = AKCFastCat.form;
            } else if (props.show.show_type === Constants.show_type.fast_dash) {
              template = ISWSFastDash.form;
            } else if (props.show.show_type === Constants.show_type.cat) {
              template = AKCCAT.form;
            } else if (props.show.show_type === Constants.show_type.fetch) {
              template = AKCFetch.form;
            }

            if (props.event._id && props.show._id) {
              apiService.getShowRunTimeSlots(props.event._id, props.show._id).then((response: IShowRunTimeSlot[]) => {
                setTimeslots(response);
                props.onTimeslotsLoaded?.(props.show._id, response);

                if (helperService.timeSlotShows.includes(props.show.show_type) && _classes.length > 0) {
                  let _time_slot = _classes[0]?.time_slot;

                  if (props.edit && props.registration) {
                    _time_slot = props.registration.time_slot;
                  }

                  let _timeslotIndex = response.findIndex((t) => t.time_slot === _time_slot);
                  setSelectedTimeslot(_timeslotIndex >= 0 ? _timeslotIndex : -1);
                }
              });
            }

          break;
          case Constants.dog_class_element.conformation:
            try {
              if (props.isJunior) {
                template = AKCConformationJunior.getForm(props.show);
              } else {
                template = AKCConformation.getForm(props.show);
              }
            } catch (error) {
              console.error('[FormBuilder] ERROR in AKCConformation.getForm:', error);
              throw error;
            }
          break;
          case Constants.show_type.obedience:
            template = AKCObedience.getForm(props.show);
          break;
          case Constants.show_type.rally_obedience:
            template = AKCRallyObedience.getForm(props.show);
          break;
          case Constants.show_type.scentwork:
            template = AKCScentwork.form;
          break;
          case Constants.show_type.farm_dog:
            template = AKCFarmDog.form;
          break;
          case Constants.show_type.att:
            template = AKCAtt.form;
          break;
          case Constants.show_type.canine_good_citizen:
            template = AKCCanineGoodCitizen.form;
          break;
          case Constants.show_type.trick_dog:
            template = AKCTrickDog.form;
          break;
          case Constants.show_type.herding:
          case Constants.show_type.pre_trial_herding:
          case Constants.show_type.instinct_test_herding:
            template = AKCHerding.getForm(props.show.show_type);
          break;
          case Constants.show_type.agility:
            template = AKCAgility.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.AHBA) {
        switch (props.show.show_type) {
          case Constants.show_type.herding:
            template = AHBAHerding.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.PWDCA) {
        switch (props.show.show_type) {
          case Constants.show_type.water_dog:
            template = PWDCAWaterDog.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.CPE) {
        switch (props.show.show_type) {
          case Constants.show_type.speedway:
            template = CPESpeedway.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.GCR) {
        switch (props.show.show_type) {
          case Constants.show_type.nitrous_50:
            template = GCRNitrous50.form;
            break;
          case Constants.show_type.nitrous_100:
            template = GCRNitrous100.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.ASFA) {
        switch (props.show.show_type) {
          case Constants.show_type.field_trial:
            template = ASFAFieldTrial.getForm(props.show);
            break;
          default:
            template = ASFAFieldTrial.getForm(props.show);
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.GENERAL) {
        switch (props.show.show_type) {
          case Constants.show_type.sniff_n_go:
            template = SniffNGo.form;
            break;
          case Constants.show_type.on_leash_run_through:
            template = OnLeashRunThrough.form;
            break;
          case Constants.show_type.dock_diving:
            template = GeneralDockDiving.form;
            break;
          case Constants.show_type.obedience_show_n_go:
            template = ObedienceShowNGo.getForm(props.show);

            if (props.event._id && props.show._id) {
              apiService.getShowRunTimeSlots(props.event._id, props.show._id).then((response: IShowRunTimeSlot[]) => {
                setTimeslots(response);
                props.onTimeslotsLoaded?.(props.show._id, response);

                const hasPerClassSlots = response.some((t) => !!t.level);
                if (hasPerClassSlots) {
                  // Per-class mode: restore selection from each class's time_slot.
                  // Keyed by the class's unique row id (not level) so that duplicate
                  // entries of the same level each restore their own independent slot.
                  const byLevel: Record<string, number> = {};
                  for (const cls of _classes) {
                    if (cls.time_slot && cls.id) {
                      const levelSlots = response.filter((t) => t.level === cls.level);
                      const idx = levelSlots.findIndex((t) => t.time_slot === cls.time_slot);
                      if (idx >= 0) byLevel[cls.id] = idx;
                    }
                  }
                  setSelectedTimeslotByLevel(byLevel);
                } else if (_classes.length > 0) {
                  let _time_slot = _classes[0]?.time_slot;
                  if (props.edit && props.registration) {
                    _time_slot = props.registration.time_slot;
                  }
                  const _timeslotIndex = response.findIndex((t) => t.time_slot === _time_slot);
                  setSelectedTimeslot(_timeslotIndex >= 0 ? _timeslotIndex : -1);
                }
              });
            }
            break;
          case Constants.show_type.general_rally:
            template = GeneralRally.form;

            if (props.event._id && props.show._id) {
              apiService.getShowRunTimeSlots(props.event._id, props.show._id).then((response: IShowRunTimeSlot[]) => {
                setTimeslots(response);
                props.onTimeslotsLoaded?.(props.show._id, response);

                const hasPerClassSlots = response.some((t) => !!t.level);
                if (hasPerClassSlots) {
                  // Keyed by the class's unique row id (not level) so that duplicate
                  // entries of the same level each restore their own independent slot.
                  const byLevel: Record<string, number> = {};
                  for (const cls of _classes) {
                    if (cls.time_slot && cls.id) {
                      const levelSlots = response.filter((t) => t.level === cls.level);
                      const idx = levelSlots.findIndex((t) => t.time_slot === cls.time_slot);
                      if (idx >= 0) byLevel[cls.id] = idx;
                    }
                  }
                  setSelectedTimeslotByLevel(byLevel);
                } else if (_classes.length > 0) {
                  let _time_slot = _classes[0]?.time_slot;
                  if (props.edit && props.registration) {
                    _time_slot = props.registration.time_slot;
                  }
                  const _timeslotIndex = response.findIndex((t) => t.time_slot === _time_slot);
                  setSelectedTimeslot(_timeslotIndex >= 0 ? _timeslotIndex : -1);
                }
              });
            }
            break;
          default:
            template = SniffNGo.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS) {
        switch (props.show.show_type) {
          case Constants.show_type.scent:
            template = CWagsScent.getForm(props.show);
            break;
          case Constants.show_type.obedience:
            template = CWagsObedience.form;
            break;
          case Constants.show_type.rally_obedience:
            template = CWagsRally.form;
            break;
          case Constants.show_type.cwags_games:
            template = CWagsGames.getForm(props.show);
            break;
          default:
            template = CWagsScent.getForm(props.show);
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.BHA) {
        switch (props.show.show_type) {
          case Constants.show_type.barn_hunt:
            template = BarnHunt.form;
            break;
          default:
            template = BarnHunt.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.FH) {
        switch (props.show.show_type) {
          case Constants.show_type.agility:
            template = FHAgility.getForm(props.show);
            break;
          case Constants.show_type.rally_obedience:
          default:
            template = FHRallyObedience.form;
            break;
        }
      } else if (props.event.sanctioning_club === Constants.sanctioning_club.CDSP) {
        switch (props.show.show_type) {
          case Constants.show_type.cdsp_video:
            template = CDSPVideo.form;
            break;
          default:
            template = CDSPVideo.form;
            break;
        }
      }
    }

    let _form:IEventRegisterRowElement[] = [];
    let duplicateOffset:number = 0;

    for (let row in template) {
      _form.push({ allowDuplicates: template[row].allowDuplicates, inputs: [] });

      for (let input in template[row].inputs) {
        // Use the input's id from template if it exists (for dropdowns like Conformation), otherwise create a random one
        let id:string = template[row].inputs[input].id || createId();

        // List of Nosework elements that can have multiple levels
        const noseworkElements = [
          Constants.dog_class_element.nosework_container,
          Constants.dog_class_element.nosework_exterior,
          Constants.dog_class_element.nosework_interior,
          Constants.dog_class_element.nosework_vehicle,
          Constants.dog_class_element.nosework_handler_descrimination
        ];

        // List of Urban Rat Race elements that can have multiple entries
        const urbanRatRaceElements = [
          Constants.dog_class_element.tubes,
          Constants.dog_class_element.unobstructed,
          Constants.dog_class_element.obstructed,
          Constants.dog_class_element.obstructed_2,
          Constants.dog_class_element.games
        ];

        let _class = _classes.filter((c) => {
          // C-WAGS FEO rows share the show_element with the regular row above them and are
          // told apart by the fixed input.section ("FEO"). Regular rows on FEO-capable show
          // types must not adopt the FEO classes' id (and vice versa).
          if (isCwagsFeoShowWideShow) {
            const rowSection = template[row].inputs[input].section;
            if (rowSection) {
              return c.show_element === template[row].inputs[input].label && c.section === rowSection;
            }
            if (c.section === Constants.dog_class_section.feo) {
              return false;
            }
          }
          if (template[row].inputs[input].label === Constants.dog_class_element.conformation_non_licensed && nonLicensed.includes(c.level)) {
            return true;
          } else if (template[row].inputs[input].label === Constants.show_type.country_rat_race && 
                     template[row].inputs[input].options && template[row].inputs[input].options.length > 0) {
            // For Country Rat Race, match by both show_element and level
            const levelOption = template[row].inputs[input].options[0];
            return c.show_element === template[row].inputs[input].label && c.level === levelOption.id;
          } else if (props.show.show_type === Constants.show_type.barn_hunt) {
            // For Barn Hunt, match by level since show_element is "Barn Hunt" for all classes
            return c.level === template[row].inputs[input].label;
          } else if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && 
                     props.show.show_type === Constants.show_type.obedience) {
            // For C-WAGS Obedience, match by level since show_element is "Obedience" for all classes
            return c.level === template[row].inputs[input].label;
          } else if (props.show.show_type === Constants.show_type.on_leash_run_through ||
                     props.show.show_type === Constants.show_type.obedience_show_n_go ||
                     props.show.show_type === Constants.show_type.general_rally) {
            // For On Leash Run Through, Obedience Show N Go, and General Rally, match by level since show_element is the element constant for all classes
            return c.level === template[row].inputs[input].label;
          } else if (noseworkElements.includes(template[row].inputs[input].label)) {
            // For Nosework with allowDuplicates, match by show_element to find all instances
            return c.show_element === template[row].inputs[input].label;
          } else if (urbanRatRaceElements.includes(template[row].inputs[input].label)) {
            // For Urban Rat Race with allowDuplicates, match by show_element to find all instances
            return c.show_element === template[row].inputs[input].label;
          } else if (props.show.show_type === Constants.show_type.scent_dog_games &&
                     template[row].inputs[input].type === "checkbox") {
            // For SDG allowDuplicates, match by both show_element and level so each game row
            // only picks up its own classes (not all SDG classes).
            const sdgLevelOption = template[row].inputs[input].options?.[0];
            return c.show_element === Constants.show_type.scent_dog_games &&
                   c.level === sdgLevelOption?.value;
          } else {
            return c.show_element === template[row].inputs[input].label;
          }
        });

        if (_class[0]) {
          id = _class[0].id;
        } else if (template[row].inputs[input].label === Constants.show_type.country_rat_race && 
                   template[row].inputs[input].options && template[row].inputs[input].options.length > 0) {
          // For Country Rat Race, create consistent ID pattern including show ID to avoid conflicts between multiple shows
          const levelOption = template[row].inputs[input].options[0];
          const showId = props.show?._id || 'default';
          id = `crr-${showId}-${levelOption.id}`;
        }

        // A section input labelled with its own element (AHBA JHD / HCT) is a class on its own
        const isStandaloneSection = !!template[row].inputs[input].showElement &&
          template[row].inputs[input].label === template[row].inputs[input].showElement;

        let sectionId = id;
        if (!isStandaloneSection && (template[row].inputs[input].label === "Section" || template[row].inputs[input].elementType === "section")) {
          // If Section is in its own row, find the ID from the previous row's first input
          if (parseInt(input) === 0 && parseInt(row) > 0) {
            const prevRowIndex = parseInt(row) + duplicateOffset - 1;
            if (_form[prevRowIndex] && _form[prevRowIndex].inputs[0]) {
              sectionId = _form[prevRowIndex].inputs[0].id;
            }
          } else if (parseInt(input) > 0) {
            // If Section is in the same row as other inputs, use the previous input's ID
            sectionId = _form[parseInt(row) + duplicateOffset].inputs[(parseInt(input) - 1)].id;
          }
        }
        
        let _input:IEventRegisterInputElement = {...template[row].inputs[input], id: sectionId};
        
        _form[parseInt(row) + duplicateOffset].inputs.push(_input);
      }

      if (_form[parseInt(row) + duplicateOffset].allowDuplicates) {
        // For BHA Barn Hunt, On Leash Run Through, Obedience Show N Go, and General Rally,
        // filter by level instead of show_element (show_element is a fixed constant for all levels)
        // For Nosework and Urban Rat Race, filter by show_element to get all instances of that element
        // For SDG, filter by show_element AND level since show_element is "Scent Dog Games" for all rows
        let hasDuplicates;
        if (props.show.show_type === Constants.show_type.scent_dog_games) {
          const sdgLevelOption = template[row].inputs[0].options?.[0];
          hasDuplicates = _classes.filter((c) =>
            c.show_element === Constants.show_type.scent_dog_games &&
            c.level === sdgLevelOption?.value
          );
        } else if (props.show.show_type === Constants.show_type.barn_hunt || 
                   props.show.show_type === Constants.show_type.on_leash_run_through ||
                   props.show.show_type === Constants.show_type.obedience_show_n_go ||
                   props.show.show_type === Constants.show_type.general_rally) {
          hasDuplicates = _classes.filter((c) => c.level === template[row].inputs[0].label);
        } else {
          hasDuplicates = _classes.filter((c) => c.show_element === template[row].inputs[0].label);
        }

        for (let d = 0; d < hasDuplicates.length - 1; d++) {
          let _duplicateRow = JSON.parse(JSON.stringify(_form[parseInt(row) + duplicateOffset]));
          _duplicateRow.allowDuplicates = false;
          _duplicateRow.duplicate = true;

          for (let i in _duplicateRow.inputs) {
            _duplicateRow.inputs[i].id = hasDuplicates[d + 1].id;
          }

          _form.push(_duplicateRow);
          duplicateOffset++;
        }
      }
    }

    setForm(_form);
  }, [props.event, props.show, props.isJunior, props.edit, props.registration]);

  useEffect(() => {
    if (!loaded) {
      let _classes:any = JSON.parse(JSON.stringify(props.classes));

      if (props.edit) {
        setElementCount(_classes.length);
      }

      createForm(_classes);
      
      setClasses(_classes);

      setLoaded(true);
    }
  }, [loaded, createForm, props.classes, props.edit, props.show, props.dog]);

  // Sync internal state when props.classes changes from parent
  useEffect(() => {
    if (loaded && JSON.stringify(props.classes) !== JSON.stringify(classes)) {
      setIsSyncingFromParent(true);
      setClasses(JSON.parse(JSON.stringify(props.classes)));
    }
  }, [props.classes, loaded, classes]);

  useEffect(() => {
    // Don't call onChange if we're just syncing from parent
    if (isSyncingFromParent) {
      setIsSyncingFromParent(false);
      return;
    }
    
    // In edit mode, prevent accidental clearing ONLY during initial mount (before loaded)
    // Once loaded, allow empty arrays (user may want to remove all classes)
    if (props.edit && classes.length === 0 && props.classes && props.classes.length > 0 && !loaded) {
      return;
    }
    
    // CRITICAL: If classes match props.classes exactly, don't call onChange
    // This prevents infinite loops when parent updates and we sync
    if (JSON.stringify(classes) === JSON.stringify(props.classes)) {
      return;
    }
    
    if (JSON.stringify(prevClasses) !== JSON.stringify(classes)) {
      let _classes = JSON.parse(JSON.stringify(classes));

      // For On Leash Run Through, add section numbers to differentiate duplicate entries
      if (props.show.show_type === Constants.show_type.on_leash_run_through) {
        // Count occurrences of each element+level combination
        const combinationCounts = new Map<string, number>();
        
        _classes.forEach((cls: IDogClassElement) => {
          const key = `${cls.show_element}-${cls.level}`;
          const currentCount = combinationCounts.get(key) || 0;
          combinationCounts.set(key, currentCount + 1);
          
          // Assign section number (1-indexed)
          cls.section = (currentCount + 1).toString();
        });
      }

      props.onChange(_classes);
    }
  }, [classes, prevClasses, isSyncingFromParent, props.edit, props.classes, props.show.show_type, loaded]);

  // Add useEffect to refresh timeslots when triggered
  useEffect(() => {
    if (props.refreshTimeslots && props.event?._id && props.show._id && helperService.timeSlotShows.includes(props.show.show_type)) {
      apiService.getShowRunTimeSlots(props.event._id, props.show._id).then((response: IShowRunTimeSlot[]) => {
        setTimeslots(response);
        
        const currentClasses = classesRef.current;
        if (currentClasses.length > 0) {
          let _time_slot = currentClasses[0]?.time_slot;
          let _timeslotIndex = response.findIndex((t) => t.time_slot === _time_slot);
          setSelectedTimeslot(_timeslotIndex >= 0 ? _timeslotIndex : -1);
        }
      });
    }
  }, [props.refreshTimeslots, props.event?._id, props.show._id, props.show.show_type]);

  const getTeamDogs = (): IDog[] => {
    const team = classes.find((c) => c.level === Constants.dog_class_element_level.team);

    if (team) {
      return props.userDogs?.filter((dog) => team.extra_arr?.includes(dog._id)) ?? [];
    }
    return [];
  }

  // C-WAGS Games: Pairs and Team rows collect a partner (free text in dog_class.extra).
  // Gated on club + show type because "Pairs"/"Team" display strings are also UKC
  // non-licensed obedience levels handled by the DogList pickers below.
  const isCWagsGamesPartnerElement = (element: string): boolean =>
    props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
    props.show.show_type === Constants.show_type.cwags_games &&
    (element === Constants.dog_class_element.cwags_games_pairs ||
      element === Constants.dog_class_element.cwags_games_team);

  const updateCWagsGamesPartner = (element: string, partner: string) => {
    setClasses((prev) =>
      prev.map((c) => (c.show_element === element ? { ...c, extra: partner } : c))
    );
  };

  const getBraceOrPairDogs = (dogClass: string): IDog[] => {
    const dogClassElement = classes.find((c) => c.level === dogClass);

    return props.userDogs?.filter((dog) => dogClassElement?.extra === dog._id) ?? [];
  }

  const getAKCBraceDogs = (): IDog[] => {
    const braceClass = classes.find((c) => c.level === Constants.dog_class_element_level.akc_brace);
    
    return props.userDogs?.filter((dog) => braceClass?.extra === dog._id) ?? [];
  }

  const asfaMetadataInputIds = [
    "asfa_first_trial_for_hound",
    "asfa_first_time_entry",
    "asfa_info_changed_since_last_trial",
    "asfa_dismissed_last_6_trials",
  ];

  const isAsfaMetadataInput = (inputId?: string) =>
    props.event?.sanctioning_club === Constants.sanctioning_club.ASFA &&
    props.show.show_type === Constants.show_type.field_trial &&
    !!inputId &&
    asfaMetadataInputIds.includes(inputId);

  const getCurrentDogId = (): string => {
    return ("_id" in props.dog ? props.dog._id : props.dog.dog_id)?.toString() || "";
  };

  const blockForMissingRegistrationFields = (level: string | undefined, retry: () => void): boolean => {
    const dog = dogRef.current as IDog;
    if (!props.event || !dog?._id) {
      return false;
    }

    const proposedChanges = dog.proposed_changes?.find((change) => change?.event_id?.toString() === props.event?._id?.toString());
    const context: RegistrationContext = {
      phase: "classSelect",
      dog,
      proposedChanges,
      sanctioningClub: props.event.sanctioning_club,
      show: props.show,
      level,
    };
    const fields = resolveMissingFields(context);
    if (fields.length === 0) {
      return false;
    }

    pendingClassRetryRef.current = retry;
    setMissingFieldsRequest({ context, fields });
    return true;
  };

  const updateClasses = (input: IEventRegisterInputElement, value: IOption, extra?: string, extra_arr?: string, extra_level?:  string, deselect?: boolean) => {
    let _classes = JSON.parse(JSON.stringify(classes));

    // For level-based show types (Barn Hunt, Obedience Show N Go, C-WAGS Obedience, C-WAGS Rally), match by level instead of show_element
    const isLevelBasedShowType =
      (props.event?.sanctioning_club === Constants.sanctioning_club.BHA && props.show.show_type === Constants.show_type.barn_hunt) ||
      (props.event?.sanctioning_club === Constants.sanctioning_club.GENERAL && props.show.show_type === Constants.show_type.obedience_show_n_go) ||
      (props.event?.sanctioning_club === Constants.sanctioning_club.GENERAL && props.show.show_type === Constants.show_type.general_rally) ||
      (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && props.show.show_type === Constants.show_type.obedience) ||
      (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && props.show.show_type === Constants.show_type.rally_obedience);

    let _class = _classes.find((c: IDogClassElement) => { 
      if (input.label === "Section" || input.elementType === "section") {
        return c.id === input.id;
      }
      if (isLevelBasedShowType) {
        return c.level === input.label && c.id === input.id;
      }
      return c.show_element === input.label && c.id === input.id;
    });

    if (extra !== undefined || extra_arr !== undefined) {
      _class = _classes.find((c: IDogClassElement) => c.id === input.id && c.level === extra_level);
    }

    // Special handling for timeslot classes (FastCAT, Fetch)
    if (input.type === "timeslot") {
      _class = _classes.find((c: IDogClassElement) => c.show_element === input.label);
    }

    switch (input.type) {
      case "checkbox":
        _classes = updateCheckboxClass(_classes, _class, input, value, extra ? extra : undefined);
      break;
      case "dropdown":
        _classes = updateDropdownClass(_classes, _class, input, value);
      break;
      case "text":
        _classes = updateTextClass(_classes, input, value);
      break;
      case "timeslot":
        _classes = updateTimeslotClass(_classes, _class, input, value, undefined, deselect);
      break;
    }

    setClasses(_classes);
  }

  const updateTimeslotClass = (oldClasses: IDogClassElement[], hasClass: IDogClassElement, input: IEventRegisterInputElement, value: IOption, extra?: string, deselect?: boolean) => {
    let _classes:IDogClassElement[] = oldClasses;

    // CAT validation: 12 months old and must have height_at_withers
    if (input.id === Constants.show_type.cat && !deselect) {
      const effectiveDob = existingChanges?.dob?.new || props.dog.dob;
      const dogAgeInMonths = moment(props.show.show_date || new Date()).diff(moment(effectiveDob), 'months');
      
      if (dogAgeInMonths < 12) {
        throwConditionError(`${props.dog.call_name} must be at least 12 months old to enter CAT. Current age: ${dogAgeInMonths} months.`);
        return oldClasses;
      }

      if (blockForMissingRegistrationFields(input.id, () => updateClasses(input, value, undefined, undefined, undefined, deselect))) {
        return oldClasses;
      }
    }

    if (input.id === Constants.show_type.fast_cat || input.id === Constants.show_type.fast_dash || input.id === Constants.show_type.cat) {
      if (deselect && hasClass) {
        // Remove the FastCAT/Fast Dash/CAT class when deselecting
        _classes = _classes.filter(c => c !== hasClass);
      } else if (hasClass) {
        // Update existing FastCAT/Fast Dash/CAT class - only update the time_slot, preserve other properties
        hasClass.time_slot = value.value;
      } else if (!deselect) {
        // Create new FastCAT/Fast Dash/CAT class only if not deselecting
        let _newClass:IDogClassElement = {
          id: Math.random().toString(36).substring(2, 8), // Generate unique ID
          show_element: input.label,
          level: input.label,
          time_slot: value.value
        };
        _classes.push(_newClass);
      }
    } else {
      if (deselect) {
        // For non-FastCAT/CAT shows, remove the timeslot
        if (oldClasses.length > 0) {
          oldClasses[0].time_slot = undefined;
        }
      } else {
        // For non-FastCAT/CAT shows, set/update the timeslot
        if (oldClasses.length > 0) {
          oldClasses[0].time_slot = value.value;
        }
      }
    }

    return _classes;
  }

  const updateCheckboxClass = (oldClasses: IDogClassElement[], hasClass: IDogClassElement, input: IEventRegisterInputElement, value: IOption, extra?: string) => {
    let _classes:IDogClassElement[] = oldClasses;

    // C-WAGS FEO rows display "Patrol 1 FEO" but the option id is the real level ("Patrol 1"),
    // which is what must be stored on the class.
    if (input.section) {
      value = { id: value.id, value: value.id };
    }
    
    // Special handling for CAT extra data fields
    if (props.show.show_type === Constants.show_type.cat) {
      if (input.id === "plan_to_earn_title") {
        // Update all CAT classes with the selected title
        _classes.forEach(c => {
          if (c.show_element === Constants.show_type.cat || c.level === Constants.show_type.cat) {
            // Toggle: if already selected, unselect
            if (c.plan_to_earn_title === value.id) {
              c.plan_to_earn_title = undefined;
              c.title = undefined;
            } else {
              c.plan_to_earn_title = value.id;
              c.title = value.value;
            }
          }
        });
        return _classes;
      }
      
      if (input.id === "is_brachycephalic") {
        // Update all CAT classes with the brachycephalic flag
        _classes.forEach(c => {
          if (c.show_element === Constants.show_type.cat || c.level === Constants.show_type.cat) {
            c.is_brachycephalic = !c.is_brachycephalic;
          }
        });
        return _classes;
      }
    }

    if (isAsfaMetadataInput(input.id)) {
      const asfaValue = value.id === "yes";
      _classes.forEach((c) => {
        if (input.id === "asfa_first_trial_for_hound") {
          c.asfa_first_trial_for_hound = asfaValue;
        } else if (input.id === "asfa_first_time_entry") {
          c.asfa_first_time_entry = asfaValue;
        } else if (input.id === "asfa_info_changed_since_last_trial") {
          c.asfa_info_changed_since_last_trial = asfaValue;
        } else if (input.id === "asfa_dismissed_last_6_trials") {
          c.asfa_dismissed_last_6_trials = asfaValue;
        }
      });
      return _classes;
    }

    // Set default section based on show type
    const initSection = () => {
      // Only certain show types use sections
      const showTypesWithSections = [
        Constants.show_type.nosework,  // UKC Nosework uses A/B sections
        Constants.show_type.country_rat_race, // Uses on_leash section
        Constants.show_type.herding,  // AKC Herding can use sections
      ];

      // C-WAGS Obedience and Rally also use sections
      const isCWagsObedience = props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                               props.show.show_type === Constants.show_type.obedience;
      const isCWagsRally = props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                           props.show.show_type === Constants.show_type.rally_obedience;

      // Country Rat Race uses "On Leash" as default section
      if (props.show?.show_type === Constants.show_type.country_rat_race) {
        return Constants.dog_class_section.on_leash;
      }

      // ABI Scent Dog Games: pick the first section not already used for this level
      // so duplicate rows automatically get an available section (e.g. Level 2 when Level 1 is taken).
      if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
          props.show?.show_type === Constants.show_type.scent_dog_games) {
        const hasSections = props.show?.show_elements?.some(se => se.section);
        if (!hasSections) return undefined;
        const usedForLevel = (_classes as IDogClassElement[])
          .filter(c => c.show_element === Constants.show_type.scent_dog_games && c.level === value.value)
          .map(c => c.section)
          .filter(Boolean) as string[];
        const firstAvailable = [
          Constants.dog_class_section.sdg_level_1,
          Constants.dog_class_section.sdg_level_2,
        ].find(s => !usedForLevel.includes(s));
        return firstAvailable ?? Constants.dog_class_section.sdg_level_1;
      }

      // 4-H Agility: section is handled per-row in FHAgility.ts via dropdowns
      if (props.event?.sanctioning_club === Constants.sanctioning_club.FH &&
          props.show?.show_type === Constants.show_type.agility) {
        return Constants.dog_class_section.fh_agility_division_a;
      }

      // If this show type uses sections, return "A" as default
      if (showTypesWithSections.includes(props.show.show_type) || isCWagsObedience || isCWagsRally) {
        return "A";
      }

      // Otherwise, no section
      return undefined;
    }

    let _newClass:IDogClassElement = {
      id: input.id,
      show_element: input.label,
      level: value.value,
      // Rows with a fixed section (C-WAGS FEO) always stamp it; otherwise use the show-type default
      section: input.section ?? initSection(),
    };

    // AKC Special Classes should be saved as Conformation
    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && 
        props.show.show_type === Constants.show_type.conformation &&
        input.label === Constants.dog_class_element.conformation_special_classes) {
      _newClass.show_element = Constants.dog_class_element.conformation;
    }

    // Barn Hunt classes should all have "Barn Hunt" as show_element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.BHA &&
        props.show.show_type === Constants.show_type.barn_hunt) {
      _newClass.show_element = Constants.dog_class_element.barn_hunt;
    }

    // C-WAGS Obedience classes should all have "Obedience" as show_element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
        props.show.show_type === Constants.show_type.obedience) {
      _newClass.show_element = Constants.dog_class_element.performance_obedience;
    }

    // C-WAGS Rally classes should all have "Rally Obedience" as show_element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
        props.show.show_type === Constants.show_type.rally_obedience) {
      _newClass.show_element = Constants.dog_class_element.performance_rally_obedience;
    }

    // 4-H Rally Obedience classes should all have "Rally Obedience" as show_element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.FH &&
        props.show.show_type === Constants.show_type.rally_obedience) {
      _newClass.show_element = Constants.show_type.rally_obedience;
    }

    // C-WAGS Games Pairs / Team: a partner typed for one level of the game applies to
    // every level of that game, so copy it onto a newly added level.
    if (isCWagsGamesPartnerElement(input.label)) {
      const partner = _classes.find((c: IDogClassElement) => c.show_element === input.label)?.extra;
      if (partner) {
        _newClass.extra = partner;
      }
    }

    if (!hasClass && blockForMissingRegistrationFields(value.value, () => updateClasses(input, value, extra))) {
      return oldClasses;
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI) {
        if (props.show.show_type === Constants.show_type.conformation) {
            let conformationSingleSelect: string[] = [
                Constants.dog_class_element_level.abi_6_9_months,
                Constants.dog_class_element_level.abi_9_12_months,
                Constants.dog_class_element_level.abi_12_18_months,
                Constants.dog_class_element_level.abi_bred_by_exibitor,
                Constants.dog_class_element_level.abi_open,
                //Constants.dog_class_element_level.abi_best_of_breed,
                Constants.dog_class_element_level.abi_jr_champion,
                Constants.dog_class_element_level.abi_champion,
                Constants.dog_class_element_level.abi_master_champion,
                Constants.dog_class_element_level.abi_grand_champion,
                Constants.dog_class_element_level.abi_supreme_champion,
                Constants.dog_class_element_level.abi_ultimate_champion,
                Constants.dog_class_element_level.abi_royal_champion,
                Constants.dog_class_element_level.abi_legendary_champion,
                Constants.dog_class_element.conformation_altered
            ];

            let hasConf = oldClasses.filter((d: IDogClassElement) => { return d.show_element === Constants.dog_class_element.conformation && conformationSingleSelect.includes(d.level); })

            if (
                value.id === Constants.dog_class_element_level.abi_brace ||
                value.id === Constants.dog_class_element_level.abi_baby_puppy
            ) {
                let hasAltered = oldClasses.filter((d: IDogClassElement) => {
                    return (
                        d.show_element === Constants.dog_class_element.conformation &&
                        d.level === Constants.dog_class_element.conformation_altered
                    );
                });

                if (hasAltered.length > 0) {
                    throwConditionError(
                        "Unable to register for " + value.id + " and " + Constants.dog_class_element.conformation_altered + " in the same registration"
                    );
                    return oldClasses;
                }
            } else if (value.id === Constants.dog_class_element.conformation_altered) {
                let hasBraceOrBabyPuppy = oldClasses.filter((d: IDogClassElement) => {
                    return (
                        d.show_element === Constants.dog_class_element.conformation &&
                        (
                            d.level === Constants.dog_class_element_level.abi_brace ||
                            d.level === Constants.dog_class_element_level.abi_baby_puppy
                        )
                    );
                });

                if (hasBraceOrBabyPuppy.length > 0) {
                    throwConditionError(
                        "Unable to register for " + value.id + " and either " + Constants.dog_class_element_level.abi_brace + " or " + Constants.dog_class_element_level.abi_baby_puppy + " in the same registration"
                    );
                    return oldClasses;
                }
            }

            if (hasConf.length > 0 && conformationSingleSelect.includes(value.id)) {
                // Find index
                const index = oldClasses.findIndex(entry =>
                    entry.id === hasConf[0].id &&
                    entry.show_element === hasConf[0].show_element &&
                    entry.level === hasConf[0].level &&
                    entry.section === hasConf[0].section
                );

                // Remove if found
                if (index !== -1) {
                    oldClasses.splice(index, 1);
                }

                if (hasConf[0].level === value.id) {
                    return oldClasses;
                }
            }
      }
    }

    if (hasClass) {
      // if (hasClass.level === Constants.dog_class_element_level.dock_jumping_air && value.id === "multiplier") {
      //   hasClass.multiplier = Number(value.value);
      // } else
      if ((hasClass.level === Constants.dog_class_element_level.brace || hasClass.level === Constants.dog_class_element_level.akc_brace || hasClass.level === Constants.dog_class_element_level.shed_dog_youth || hasClass.level === Constants.dog_class_element_level.pairs || hasClass.level === Constants.dog_class_element_level.novice_pairs || hasClass.level === Constants.dog_class_element_level.advanced_pairs) && value.id === "extra") {
        hasClass.extra = value.value;
      } else if ([
        Constants.dog_class_element_level.team,
        Constants.dog_class_element_level.abi_stud_dog,
        Constants.dog_class_element_level.abi_brood_bitch,
        Constants.dog_class_element_level.isws_stud_dog,
        Constants.dog_class_element_level.isws_brood_bitch,
      ].includes(hasClass.level) && value.id.includes("extra_arr")) {
        let _index = value.id.split('_');

        if (hasClass.extra_arr === undefined) {
          hasClass.extra_arr = ["", "", ""];
        }

        if (hasClass.extra_arr) {
          // dog list component case
          if (isNaN(Number(_index[2]))) {
            hasClass.extra_arr = value.value.split(",");
          // raw number inputs case
          } else {
            hasClass.extra_arr[parseInt(_index[2])] = value.value;
          }
        }
      } else if (input.multiSelect) {
        // Special handling for AKC Special Classes and BHA Barn Hunt
        let _hasMultiSelectClass;
        if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
            props.show.show_type === Constants.show_type.scent_dog_games) {
          // SDG rows with allowDuplicates share show_element+level across rows.
          // Match by input.id so each row independently toggles its own class.
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.id === input.id && c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && 
            props.show.show_type === Constants.show_type.conformation &&
            input.label === Constants.dog_class_element.conformation_special_classes) {
          // For special classes, check by level only since show_element is overridden to "Conformation"
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.show.show_type === Constants.show_type.barn_hunt) {
          // For Barn Hunt, check by level only since show_element is "Barn Hunt" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                   props.show.show_type === Constants.show_type.obedience) {
          // For C-WAGS Obedience, check by level only since show_element is "C-WAGS Obedience" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                   props.show.show_type === Constants.show_type.rally_obedience) {
          // For C-WAGS Rally, check by level only since show_element is "C-WAGS Rally" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else {
          // Regular multi-select logic (section-aware on C-WAGS Scent / Games so a regular row
          // and its FEO row toggle independently)
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) =>
            c.show_element === input.label && c.level === value.id && sameSectionAsInput(c, input)
          );
        }

        if (_hasMultiSelectClass.length > 0) {
          _classes.splice(_classes.indexOf(_hasMultiSelectClass[0]), 1);
        } else {
          // Check for mutual exclusivity before adding
          if (input.mutuallyExclusive) {
            for (const exclusiveGroup of input.mutuallyExclusive) {
              if (exclusiveGroup.includes(value.id)) {
                // Remove any other options from this exclusive group
                const otherOptions = exclusiveGroup.filter(option => option !== value.id);

                for (const option of otherOptions) {
                  const existingClass = _classes.find((c: IDogClassElement) =>
                    c.show_element === input.label && c.level === option
                  );
                  if (existingClass) {
                    _classes.splice(_classes.indexOf(existingClass), 1);
                  }
                }
                break;
              }
            }
          }
          _classes.push(_newClass);
        }
      } else if (!input.multiSelect) {
        // Handle single-select behavior for AKC Conformation
        if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && 
            props.show.show_type === Constants.show_type.conformation &&
            input.label === Constants.dog_class_element.conformation) {
          
          // Find any existing regular conformation class
          let existingConformationClass = _classes.filter((c: IDogClassElement) => 
            c.show_element === Constants.dog_class_element.conformation
          );
          
          // If clicking the same class that's already selected, deselect it
          if (existingConformationClass.length > 0 && existingConformationClass[0].level === value.value) {
            _classes.splice(_classes.indexOf(existingConformationClass[0]), 1);
          } else {
            // Remove existing conformation class if any
            if (existingConformationClass.length > 0) {
              _classes.splice(_classes.indexOf(existingConformationClass[0]), 1);
            }
            // Add the new class
            _classes.push(_newClass);
          }
        } else {
          // Default single-select behavior for other cases
          // Check if this is a checkbox that should allow deselection
          const isDeselectable = input.type === 'checkbox';

          // For Barn Hunt, C-WAGS Obedience, and C-WAGS Rally, the input.label is the level
          // and show_element is a fixed constant ("Barn Hunt" / "Obedience" / "Rally Obedience").
          const isLevelBasedShowType = 
            (props.event?.sanctioning_club === Constants.sanctioning_club.BHA && props.show.show_type === Constants.show_type.barn_hunt) ||
            (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && props.show.show_type === Constants.show_type.obedience) ||
            (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS && props.show.show_type === Constants.show_type.rally_obedience);
          
          if (props.edit || isDeselectable) {
            // Toggle behavior: remove if exists, add if doesn't exist
            // For single-select checkboxes in edit mode (ChangeElements), find by show_element and level
            // instead of relying on hasClass which may be stale due to ID mismatch
            const existingClass = isLevelBasedShowType
              ? _classes.find((c: IDogClassElement) => c.level === value.value)
              : _classes.find((c: IDogClassElement) => c.show_element === input.label && c.level === value.value);
            
            if (existingClass) {
              // Class with this exact level exists, remove it (deselect)
              _classes.splice(_classes.indexOf(existingClass), 1);
            } else {
              // Class doesn't exist, add it
              // But first, remove any other class with the same show_element (single-select behavior)
              const otherClass = isLevelBasedShowType
                ? _classes.find((c: IDogClassElement) => c.level === input.label)
                : _classes.find((c: IDogClassElement) => c.show_element === input.label);
              if (otherClass) {
                _classes.splice(_classes.indexOf(otherClass), 1);
              }
              // For Fetch: carry over the already-selected timeslot so clicking class before
              // or after the timeslot both work correctly
              if (props.show.show_type === Constants.show_type.fetch) {
                const carryTimeslot = (selectedTimeslot >= 0 && timeslots[selectedTimeslot])
                  ? timeslots[selectedTimeslot].time_slot
                  : otherClass?.time_slot;
                if (carryTimeslot) _newClass.time_slot = carryTimeslot;
              }
              _classes.push(_newClass);
            }
          } else {
            // Replacement behavior: remove existing and add new
            _classes.splice(_classes.indexOf(hasClass), 1);
            _classes.push(_newClass);
          }
        }
      }
    } else {
      // No existing class found with this show_element
      // For multiSelect inputs (like Special Classes), check if we already have this level
      if (input.multiSelect) {
        // Special handling for AKC Special Classes and BHA Barn Hunt
        let _hasMultiSelectClass;
        if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
            props.show.show_type === Constants.show_type.scent_dog_games) {
          // SDG duplicate rows share show_element+level. Each row has a unique input.id so
          // we must scope the check to that id — otherwise removing row 2's class inadvertently
          // removes row 1's class (which has the same show_element+level).
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.id === input.id && c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && 
            props.show.show_type === Constants.show_type.conformation &&
            input.label === Constants.dog_class_element.conformation_special_classes) {
          // For special classes, check by level only since show_element is overridden to "Conformation"
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.show.show_type === Constants.show_type.barn_hunt) {
          // For Barn Hunt, check by level only since show_element is "Barn Hunt" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                   props.show.show_type === Constants.show_type.obedience) {
          // For C-WAGS Obedience, check by level only since show_element is "C-WAGS Obedience" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else if (props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                   props.show.show_type === Constants.show_type.rally_obedience) {
          // For C-WAGS Rally, check by level only since show_element is "C-WAGS Rally" for all classes
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) => c.level === value.id);
        } else {
          // Regular multi-select logic (section-aware on C-WAGS Scent / Games so a regular row
          // and its FEO row toggle independently)
          _hasMultiSelectClass = _classes.filter((c: IDogClassElement) =>
            c.show_element === input.label && c.level === value.id && sameSectionAsInput(c, input)
          );
        }

        if (_hasMultiSelectClass.length > 0) {
          // Class exists, remove it (toggle off)
          _classes.splice(_classes.indexOf(_hasMultiSelectClass[0]), 1);
        } else {
          // For SDG duplicate rows, auto-pick the first section not already used for this level
          // so the new class doesn't collide with the existing row's section.
          if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
              props.show.show_type === Constants.show_type.scent_dog_games &&
              _newClass.level) {
            const usedForLevel = (_classes as IDogClassElement[])
              .filter(c => c.show_element === Constants.show_type.scent_dog_games && c.level === _newClass.level)
              .map(c => c.section)
              .filter(Boolean) as string[];
            const sdgSections = [Constants.dog_class_section.sdg_level_1, Constants.dog_class_section.sdg_level_2];
            const firstAvailable = sdgSections.find(s => !usedForLevel.includes(s));
            if (firstAvailable) {
              _newClass.section = firstAvailable;
            }
          }
          _classes.push(_newClass);
        }
      } else {
        // Single select, just add it
        // For Fetch: carry over the already-selected timeslot so clicking class before
        // or after the timeslot both work correctly
        if (props.show.show_type === Constants.show_type.fetch &&
            selectedTimeslot >= 0 && timeslots[selectedTimeslot]) {
          _newClass.time_slot = timeslots[selectedTimeslot].time_slot;
        }
        _classes.push(_newClass);
      }
    }

    if (props.show.show_type === Constants.show_type.scentwork) {
      _classes = checkScentworkConditions(input.label, value.value, _classes);
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC && props.show.show_type === Constants.show_type.agility) {
      _classes = checkUKCAgilityConditions(input.label, value.value, _classes);
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.show_type === Constants.show_type.agility) {
      _classes = checkAKCAgilityConditions(input.label, value.value, _classes);
    }

    switch (input.label) {
      case Constants.dog_class_element.performance_rally_obedience:
        _classes = checkRallyConditions(value.value, _classes, _newClass.section);
      break;
      case Constants.dog_class_element.performance_obedience:
        _classes = checkObedienceConditions(value.value, _classes);
      break;
      case Constants.dog_class_element.conformation_non_licensed:
        _classes = checkConformationConditions(value.value, _classes);
      break;
    }

    // SDG section conflict resolution: if two entries for the same level ended up with
    // the same section (e.g. both "Level 1"), reassign the one with the *larger* id index
    // (i.e. the one just added) to the first available section.
    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
      props.show.show_type === Constants.show_type.scent_dog_games
    ) {
      const sdgSectionOrder = [
        Constants.dog_class_section.sdg_level_1,
        Constants.dog_class_section.sdg_level_2,
      ];
      // Group SDG entries by level and fix any section conflicts
      const sdgEntries = (_classes as IDogClassElement[]).filter(
        c => c.show_element === Constants.show_type.scent_dog_games
      );
      const seenSections: Record<string, string[]> = {}; // level → sections already assigned
      sdgEntries.forEach(c => {
        const lvl = c.level ?? "";
        if (!seenSections[lvl]) seenSections[lvl] = [];
        if (c.section && seenSections[lvl].includes(c.section)) {
          // Conflict: assign the first section NOT already taken
          const available = sdgSectionOrder.find(s => !seenSections[lvl].includes(s));
          if (available) c.section = available;
        }
        if (c.section) seenSections[lvl].push(c.section);
      });
    }

    return _classes;
  }

  const updateDropdownClass = (oldClasses: IDogClassElement[], hasClass: IDogClassElement, input: IEventRegisterInputElement, value: IOption) => {
    let _classes:IDogClassElement[] = oldClasses;

    // Special handling for CAT title dropdown
    if (props.show.show_type === Constants.show_type.cat && input.id === "plan_to_earn_title") {
      // Update all CAT classes with the selected title
      _classes.forEach(c => {
        if (c.show_element === Constants.show_type.cat || c.level === Constants.show_type.cat) {
          if (value.id === "no") {
            c.plan_to_earn_title = undefined;
            c.title = undefined;
          } else {
            c.plan_to_earn_title = value.id;
            c.title = value.value;
          }
        }
      });
      return _classes;
    }

    if (props.event?.sanctioning_club === Constants.sanctioning_club.ABI) {
      if (props.show.show_type === Constants.show_type.conformation) {
        if (value.id === Constants.dog_class_element_level.abi_altered) {
          let hasBraceOrBabyPuppy = oldClasses.filter((d: IDogClassElement) => { return d.show_element === Constants.dog_class_element.conformation_non_licensed && (d.level === Constants.dog_class_element_level.abi_baby_puppy || d.level === Constants.dog_class_element_level.abi_brace); });

          if (hasBraceOrBabyPuppy.length > 0) {
            throwConditionError("Unable to register for " + value.id + " and " + Constants.dog_class_element.conformation_altered + " in the same registration, "+ hasBraceOrBabyPuppy[0].level + " has been removed");
            _classes.splice(_classes.indexOf(hasBraceOrBabyPuppy[0]), 1);
          }
        }
      }
    }

    if (value.value && value.value !== "None" && blockForMissingRegistrationFields(value.value, () => updateClasses(input, value))) {
      return oldClasses;
    }

    // Set default section based on show type
    let defaultSection = 'None';
    if (props.show?.show_type === Constants.show_type.nosework) {
      defaultSection = 'A';
    } else if (props.show?.show_type === Constants.show_type.country_rat_race) {
      defaultSection = Constants.dog_class_section.on_leash;
    }

    let _newClass:IDogClassElement = {
      id: input.id,
      show_element: input.label,
      level: value.value,
      section: defaultSection
    };

    // Special handling for On Leash Run Through, Obedience Show N Go, General Rally, Barn Hunt, C-WAGS Obedience, and C-WAGS Rally - show_element should be the constant, level is the input label
    if (props.show.show_type === Constants.show_type.on_leash_run_through) {
      _newClass.show_element = Constants.dog_class_element.on_leash_run_through;
      _newClass.level = input.label; // "Novice" or "Intermediate"
    } else if (props.show.show_type === Constants.show_type.obedience_show_n_go) {
      _newClass.show_element = Constants.dog_class_element.performance_obedience;
      _newClass.level = input.label; // "Novice", "Open", or "Utility"
    } else if (props.show.show_type === Constants.show_type.general_rally) {
      _newClass.show_element = Constants.dog_class_element.performance_rally_obedience;
      _newClass.level = input.label; // "Novice", "Intermediate", etc.
    } else if (props.show.show_type === Constants.show_type.barn_hunt) {
      _newClass.show_element = Constants.dog_class_element.barn_hunt;
      _newClass.level = input.label;
    } else if (isAHBATestInput(input)) {
      _newClass.show_element = input.showElement as string;
      _newClass.section = value.value;
    }

    let conformationLabels:string[] = [
      Constants.dog_class_element.conformation,
      Constants.dog_class_element.conformation_altered
    ]

    if (hasClass) {
      if (value.value === "None" && (isAHBATestInput(input) || (input.label !== "Section" && input.elementType !== "section"))) {
        _classes.splice(_classes.indexOf(hasClass), 1);
      } else {
        let _class = _classes.filter((c: IDogClassElement) => c.id === input.id)[0];

        if (isAHBATestInput(input)) {
          _class.level = value.value;
          _class.section = value.value;
        } else if (input.label !== "Section" && input.elementType !== "section") {
          _class.level = value.value;

          // A stock picked for the old level may not be offered at the new one
          if (isAHBAHerdingShow && _class.section && _class.section !== "None") {
            const stockOffered = props.show.show_elements.some((se: IShowElement) =>
              se.show_element === _class.show_element && se.level === value.value && se.section === _class.section
            );
            if (!stockOffered) {
              _class.section = "None";
            }
          }
        } else {
          _class.section = value.value;
        }

        if (conformationLabels.includes(input.label)) {
          _classes = checkConformationConditions(input.label, _classes);
        }
      }
    } else {
      if (input.label !== "Section" && value.value !== "None") {
        if (conformationLabels.includes(input.label)) {
          _classes = checkConformationConditions(input.label, _classes);
        }

        _classes.push(_newClass);
      }
    }

    return _classes;
  }

  const updateTextClass = (oldClasses: IDogClassElement[], input: IEventRegisterInputElement, value: IOption) => {
    const _classes: IDogClassElement[] = oldClasses;

    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.ASFA &&
      props.show.show_type === Constants.show_type.field_trial &&
      input.id === "asfa_info_changed_regarding"
    ) {
      _classes.forEach((c) => {
        c.asfa_info_changed_regarding = value.value;
      });
    }

    return _classes;
  }

  const checkUKCAgilityConditions = (label: string, value: string, _classes: IDogClassElement[]) => {
    switch(value) {
      case Constants.dog_class_element_level.beginner_a:
        let hasBeginnerB = _classes.find((c) => label === c.show_element && c.level === Constants.dog_class_element_level.beginner_b);

        if (hasBeginnerB) {
          _classes.splice(_classes.indexOf(hasBeginnerB), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.beginner_a +" and "+ Constants.dog_class_element_level.beginner_b +". "+ Constants.dog_class_element_level.beginner_b +" option has been reset.");
        }
        break;
      case Constants.dog_class_element_level.beginner_b:
        let hasBeginnerA = _classes.find((c) => label === c.show_element && c.level === Constants.dog_class_element_level.beginner_a);

        if (hasBeginnerA) {
          _classes.splice(_classes.indexOf(hasBeginnerA), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.beginner_a +" and "+ Constants.dog_class_element_level.beginner_b +". "+ Constants.dog_class_element_level.beginner_a +" option has been reset.");
        }
        break;

      case Constants.dog_class_element_level.intermediate_a:
        let hasIntermediateB = _classes.find((c) => label === c.show_element && c.level === Constants.dog_class_element_level.intermediate_b);

        if (hasIntermediateB) {
          _classes.splice(_classes.indexOf(hasIntermediateB), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.intermediate_a +" and "+ Constants.dog_class_element_level.intermediate_b +". "+ Constants.dog_class_element_level.intermediate_b +" option has been reset.");
        }
        break;
      case Constants.dog_class_element_level.intermediate_b:
        let hasIntermediateA = _classes.find((c) => label === c.show_element && c.level === Constants.dog_class_element_level.intermediate_a);

        if (hasIntermediateA) {
          _classes.splice(_classes.indexOf(hasIntermediateA), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.intermediate_a +" and "+ Constants.dog_class_element_level.intermediate_b +". "+ Constants.dog_class_element_level.intermediate_a +" option has been reset.");
        }
        break;
    }

    return _classes;
  }

  const checkAKCAgilityConditions = (label: string, value: string, _classes: IDogClassElement[]) => {
    switch(value) {
      case Constants.dog_class_element_level.novice_a:
      case Constants.dog_class_element_level.novice_b:
      case Constants.dog_class_element_level.akc_open:
      case Constants.dog_class_element_level.excellent:
      case Constants.dog_class_element_level.master:
        const hasPreferred = _classes.filter((c) => {
          return label === c.show_element && c.level.includes("Preferred");
        });

        if (hasPreferred.length) {
          for (const preferred of hasPreferred) {
            _classes.splice(_classes.indexOf(preferred), 1);
          }
          throwConditionError("You can't register for Regular and Preferred classes at same time, option has been reset.");
        }
        break;

      case Constants.dog_class_element_level.novice_preferred:
      case Constants.dog_class_element_level.open_preferred:
      case Constants.dog_class_element_level.excellent_preferred:
      case Constants.dog_class_element_level.master_preferred:
        const hasRegular = _classes.filter((c) => {
          return label === c.show_element && !c.level.includes("Preferred");
        });

        if (hasRegular.length) {
          for (const regular of hasRegular) {
            _classes.splice(_classes.indexOf(regular), 1);
          }
          throwConditionError("You can't register for Preferred and Regular classes at same time, option has been reset.");
        }
        break;
    }

    return _classes;
  }

  const checkScentworkConditions = (label: string, value:string, _classes: IDogClassElement[]) => {
    switch(value) {
      //Novice
      case Constants.dog_class_element_level.novice_a:
        let hasNoviceB = _classes.filter((c: IDogClassElement) => label === c.show_element && c.level === Constants.dog_class_element_level.novice_b);

        if (hasNoviceB.length > 0) {
          _classes.splice(_classes.indexOf(hasNoviceB[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.novice_a +" and "+ Constants.dog_class_element_level.novice_b +". "+ Constants.dog_class_element_level.novice_b +" option has been reset.");
        }
      break;
      case Constants.dog_class_element_level.novice_b:
        let hasNoviceA = _classes.filter((c: IDogClassElement) => label === c.show_element && c.level === Constants.dog_class_element_level.novice_a);

        if (hasNoviceA.length > 0) {
          _classes.splice(_classes.indexOf(hasNoviceA[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.novice_b +" and "+ Constants.dog_class_element_level.novice_a +". "+ Constants.dog_class_element_level.novice_a +" option has been reset.");
        }
      break;
    }

    let hasMoreThanTwo = _classes.filter((c: IDogClassElement) => label === c.show_element);

    if (hasMoreThanTwo.length > 2) {
      throwConditionError("You can only enter two levels per Scentwork element. " + hasMoreThanTwo[0].level + " has been removed for "+ hasMoreThanTwo[0].show_element +".");
      _classes.splice(_classes.indexOf(hasMoreThanTwo[0]), 1);
      
    }

    return _classes;
  }

    const checkConformationConditions = (label: string, _classes: IDogClassElement[], dog?: IDog | IDogRegistrationInfo) => {
    // List of regular puppy class levels
    const puppyClassLevels = [
      Constants.dog_class_element_level.akc_4_6_month_puppy,
      Constants.dog_class_element_level.akc_6_9_month_puppy,
      Constants.dog_class_element_level.akc_9_12_month_puppy,
      Constants.dog_class_element_level.akc_12_18_month_puppy,
      Constants.dog_class_element_level.akc_18_24_month_puppy,
      Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
      Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
      Constants.dog_class_element_level.akc_amateur_owner_handler,
      Constants.dog_class_element_level.akc_american_bred,
      Constants.dog_class_element_level.akc_open,
      Constants.dog_class_element_level.akc_best_of_breed,
      Constants.dog_class_element_level.akc_veteran_6_8_years,
      Constants.dog_class_element_level.akc_veteran_8_10_years,
      Constants.dog_class_element_level.akc_veteran_8_11_years,
      Constants.dog_class_element_level.akc_veteran_10_12_years,
      Constants.dog_class_element_level.akc_veteran_11_plus_years,
      Constants.dog_class_element_level.akc_veteran_7_plus_years,
      Constants.dog_class_element_level.akc_veteran_8_plus_years,
      Constants.dog_class_element_level.akc_veteran_13_plus_years,
      Constants.dog_class_element_level.akc_veteran_7_10_years,
      Constants.dog_class_element_level.akc_veteran_7_9_years,
      Constants.dog_class_element_level.akc_veteran_9_plus_years,
      Constants.dog_class_element_level.akc_veteran_12_plus_years,
      Constants.dog_class_element_level.akc_coursing,
    ];
    
    // List of age-specific puppy sweepstakes classes
    const puppySweepstakesLevels = [
      Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
    ];
    
    // REMOVED: Puppy sweepstakes no longer require a regular class to be selected
    // This allows standalone puppy sweepstakes entries per customer requirements
    // (Previously enforced that puppy sweepstakes required a regular conformation class)
    /*
    // Check for old puppy sweepstakes (backward compatibility)
    const hasOldPuppySweepstakes = _classes.some(c => c.level === Constants.dog_class_element_level.akc_puppy_sweepstakes);
    if (hasOldPuppySweepstakes) {
      const hasRegularClass = _classes.some(c => puppyClassLevels.includes(c.level));
      if (!hasRegularClass) {
        // Remove old puppy sweepstakes and throw error
        const idx = _classes.findIndex(c => c.level === Constants.dog_class_element_level.akc_puppy_sweepstakes);
        if (idx !== -1) _classes.splice(idx, 1);
        throwConditionError("You must enter a regular class to enter Puppy Sweepstakes. Puppy Sweepstakes option has been reset.");
      }
    }
    
    // Check for new age-specific puppy sweepstakes
    const hasNewPuppySweepstakes = _classes.some(c => puppySweepstakesLevels.includes(c.level));
    if (hasNewPuppySweepstakes) {
      const hasRegularClass = _classes.some(c => puppyClassLevels.includes(c.level));
      if (!hasRegularClass) {
        // Remove puppy sweepstakes classes and throw error
        puppySweepstakesLevels.forEach(level => {
          const idx = _classes.findIndex(c => c.level === level);
          if (idx !== -1) _classes.splice(idx, 1);
        });
        throwConditionError("You must enter a regular class to enter Puppy Sweepstakes. Puppy Sweepstakes options have been reset.");
      }
    }
    */

    switch (label) {
      case Constants.dog_class_element.conformation: {
        let hasAlteredConf = _classes.filter((c: IDogClassElement) => c.show_element === Constants.dog_class_element.conformation_altered);
        let hasNovicePuppy = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element.novice_puppy);

        // In edit mode during initial load, don't auto-remove conflicting classes
        // This prevents automatic switching when dropdowns render for the first time
        // Once loaded, allow normal conflict resolution for user interactions
        if (!loaded && props.edit && hasAlteredConf.length > 0) {
          return _classes;
        }

        if (hasAlteredConf.length > 0) {
          _classes.splice(_classes.indexOf(hasAlteredConf[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.conformation +" and "+ Constants.dog_class_element.conformation_altered +". "+ Constants.dog_class_element.conformation_altered +" option has been reset.");
        } else if (hasNovicePuppy.length > 0) {
          _classes.splice(_classes.indexOf(hasNovicePuppy[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.conformation +" and "+ Constants.dog_class_element.novice_puppy +". "+ Constants.dog_class_element.novice_puppy +" option has been reset.");
        }

        break;
      }
      case Constants.dog_class_element.conformation_altered: {
        let hasConf =  _classes.filter((c: IDogClassElement) => c.show_element === Constants.dog_class_element.conformation);
        let hasNovicePuppy = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element.novice_puppy);

        // In edit mode during initial load, don't auto-remove conflicting classes
        // This prevents automatic switching when dropdowns render for the first time
        // Once loaded, allow normal conflict resolution for user interactions
        if (!loaded && props.edit && hasConf.length > 0) {
          return _classes;
        }

        if (hasConf.length > 0) {
          _classes.splice(_classes.indexOf(hasConf[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.conformation_altered +" and "+ Constants.dog_class_element.conformation +". "+ Constants.dog_class_element.conformation +" option has been reset.");
        } else if (hasNovicePuppy.length > 0) {
          _classes.splice(_classes.indexOf(hasNovicePuppy[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.conformation_altered +" and "+ Constants.dog_class_element.novice_puppy +". "+ Constants.dog_class_element.novice_puppy +" option has been reset.");
        }

        break;
      }
      case Constants.dog_class_element.novice_puppy: {
        let hasVeteran = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element.veteran);

        if (hasVeteran.length > 0) {
          _classes.splice(_classes.indexOf(hasVeteran[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.novice_puppy +" and "+ Constants.dog_class_element.veteran +". "+ Constants.dog_class_element.veteran +" option has been reset.");            
        }

        let hasConf =  _classes.filter((c: IDogClassElement) => c.show_element === "Conformation");

        if (hasConf.length > 0) {
          _classes.splice(_classes.indexOf(hasConf[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.novice_puppy +" and "+ Constants.dog_class_element.conformation +". "+ Constants.dog_class_element.conformation +" option has been reset."); 
        }

        let hasAlteredConf = _classes.filter((c: IDogClassElement) => c.show_element === "Altered Conformation");

        if (hasAlteredConf.length > 0) {
          _classes.splice(_classes.indexOf(hasAlteredConf[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.novice_puppy +" and "+ Constants.dog_class_element.conformation_altered +". "+ Constants.dog_class_element.conformation_altered +" option has been reset."); 
        }

        break;
      }
      case Constants.dog_class_element.veteran: {
        let hasNovicePuppy = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element.novice_puppy);

        if (hasNovicePuppy.length > 0) {
          _classes.splice(_classes.indexOf(hasNovicePuppy[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element.veteran +" and "+ Constants.dog_class_element.novice_puppy +". "+ Constants.dog_class_element.novice_puppy +" option has been reset.");            
        }

        break;
      }
    }

    return _classes;
  }

  // UKC Rally: a dog may enter either the A or the B section of a level, never both.
  // Works for legacy "RO1 B" values and canonical level + section pairs.
  const checkRallyConditions = (value: string, _classes: IDogClassElement[], section?: string | null) => {
    const picked = parseUkcRallyClass(value, section);
    const pairedSections = [Constants.dog_class_section.ukc_rally_a, Constants.dog_class_section.ukc_rally_b];

    if (!pairedSections.includes(picked.section)) {
      return _classes;
    }

    const otherSection = picked.section === Constants.dog_class_section.ukc_rally_a
      ? Constants.dog_class_section.ukc_rally_b
      : Constants.dog_class_section.ukc_rally_a;
    const conflict = _classes.find((c: IDogClassElement) =>
      isSameUkcRallyClass(c, { level: picked.level, section: otherSection })
    );

    if (conflict) {
      const pickedLabel = ukcRallyClassLabel(picked.level, picked.section);
      const conflictLabel = ukcRallyClassLabel(picked.level, otherSection);
      _classes.splice(_classes.indexOf(conflict), 1);
      throwConditionError("You can't register for "+ pickedLabel +" and "+ conflictLabel +". "+ conflictLabel +" option has been reset.");
    }

    return _classes;
  }

  const checkObedienceConditions = (value: string, _classes: IDogClassElement[]) => {
    switch(value) {
      //Novice
      case Constants.dog_class_element_level.novice_a:
        let hasNoviceB = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.novice_b);

        if (hasNoviceB.length > 0) {
          _classes.splice(_classes.indexOf(hasNoviceB[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.novice_a +" and "+ Constants.dog_class_element_level.novice_b +". "+ Constants.dog_class_element_level.novice_b +" option has been reset.");
        }
      break;
      case Constants.dog_class_element_level.novice_b:
        let hasNoviceA = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.novice_a);

        if (hasNoviceA.length > 0) {
          _classes.splice(_classes.indexOf(hasNoviceA[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.novice_b +" and "+ Constants.dog_class_element_level.novice_a +". "+ Constants.dog_class_element_level.novice_a +" option has been reset.");
        }
      break;

      //Open
      case Constants.dog_class_element_level.open_a:
        let hasOpenB = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.open_b);

        if (hasOpenB.length > 0) {
          _classes.splice(_classes.indexOf(hasOpenB[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.open_a +" and "+ Constants.dog_class_element_level.open_b +". "+ Constants.dog_class_element_level.open_b +" option has been reset.");
        }
      break;
      case Constants.dog_class_element_level.open_b:
        let hasOpenA = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.open_a);

        if (hasOpenA.length > 0) {
          _classes.splice(_classes.indexOf(hasOpenA[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.open_b +" and "+ Constants.dog_class_element_level.open_a +". "+ Constants.dog_class_element_level.open_a +" option has been reset.");
        }
      break;

      //Utility
      case Constants.dog_class_element_level.utility_a:
        let hasUtilityB = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.utility_b);

        if (hasUtilityB.length > 0) {
          _classes.splice(_classes.indexOf(hasUtilityB[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.utility_a +" and "+ Constants.dog_class_element_level.utility_b +". "+ Constants.dog_class_element_level.utility_b +" option has been reset.");
        }
      break;
      case Constants.dog_class_element_level.utility_b:
        let hasUtilityA = _classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.utility_a);

        if (hasUtilityA.length > 0) {
          _classes.splice(_classes.indexOf(hasUtilityA[0]), 1);
          throwConditionError("You can't register for "+ Constants.dog_class_element_level.utility_b +" and "+ Constants.dog_class_element_level.utility_a +". "+ Constants.dog_class_element_level.utility_a +" option has been reset.");
        }
      break;
    }

    return _classes;
  }

  const throwConditionError = (msg: string, includeDogEditLink: boolean = false) => {
    toast.show({
      title: "Show Registration",
      content: msg,
      duration: 10000,
      eventId: props.event?._id,
      proposedChanges: existingChanges,
      type: "fail",
      ...(includeDogEditLink && {
        dog: props.dog as IDog,
        onDogEdit: () => {
          // Use the refresh-only callback if available, otherwise fall back to regular onEditDog
          if (props.onRefreshDogData) {
            props.onRefreshDogData();
          } else if (props.onEditDog && 'club_registrations' in props.dog) {
            props.onEditDog(props.dog as IDog);
          }
        },
        canEdit: !!props.onEditDog
      })
    });
  }

  const getValue = (input: IEventRegisterInputElement) => {
    // Special handling for CAT title dropdown
    if (props.show.show_type === Constants.show_type.cat && input.id === "plan_to_earn_title") {
      // Find any CAT class and return its title
      const catClass = classes.find(c => c.show_element === Constants.show_type.cat || c.level === Constants.show_type.cat);
      if (catClass && catClass.plan_to_earn_title) {
        return { value: catClass.title || catClass.plan_to_earn_title, id: catClass.plan_to_earn_title };
      }
      return { value: "No", id: "no" };
    }

    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.ASFA &&
      props.show.show_type === Constants.show_type.field_trial &&
      input.id === "asfa_info_changed_regarding"
    ) {
      const regarding = classes.find((c) => c.asfa_info_changed_regarding !== undefined)?.asfa_info_changed_regarding;
      return { value: regarding || "", id: regarding || "" };
    }

    let _class = classes.filter((c) => c.id === input.id)[0];
    let value:IOption = { value: "None", id: "None" };

    if (_class) {
      if (isAHBATestInput(input)) {
        const stock = getAHBATestStock(_class);
        value = { value: stock, id: stock };
      } else if (input.label === "Section" || input.elementType === "section") {
        // Always return the section value for Section/Stock dropdowns.
        // If section is falsy/unset, return "None" — never fall through to the level name.
        const section = _class.section && _class.section !== "" ? _class.section : "None";
        value = { value: section, id: section };
      } else {
        value = { value: _class.level, id: _class.level };
      }
    } else {
      // For SDG section dropdowns on a row that has no class yet (duplicate row before checkbox is checked),
      // show the first available section instead of the default so the UI is always consistent.
      if (
        props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
        props.show.show_type === Constants.show_type.scent_dog_games &&
        input.label === "Section" &&
        (input as any).sdgLevelConstant
      ) {
        const sdgLevelConstant = (input as any).sdgLevelConstant as string;
        const usedSections = (classes as IDogClassElement[])
          .filter(c => c.show_element === Constants.show_type.scent_dog_games && c.level === sdgLevelConstant)
          .map(c => c.section)
          .filter(Boolean) as string[];
        const firstAvailable = [
          Constants.dog_class_section.sdg_level_1,
          Constants.dog_class_section.sdg_level_2,
        ].find(s => !usedSections.includes(s));
        if (firstAvailable) {
          return { value: firstAvailable, id: firstAvailable };
        }
      }
      if (input.defaultValue) {
        value = { value: input.defaultValue, id: input.defaultValue };
      }
    }

    return value;
  }

  const canAddMoreLevels = (row: IEventRegisterRowElement): boolean => {
    const isUKCNoseworkEvent = props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
      props.show?.show_type === Constants.show_type.nosework;

    if (!isUKCNoseworkEvent || !props.show?.show_elements) return true;

    const levelInput = row.inputs.find((input: IEventRegisterInputElement) => input.label !== "Section" && input.type === "dropdown");
    if (!levelInput) return true;

    const elementLabel = levelInput.label;

    const hasLegacyEntry = props.show.show_elements.some(
      (se: IShowElement) => se.show_element === elementLabel && !se.level
    );
    if (hasLegacyEntry) return true;

    const offeredLevels = props.show.show_elements
      .filter((se: IShowElement) => se.show_element === elementLabel && se.level)
      .map((se: IShowElement) => se.level as string);

    if (offeredLevels.length === 0) return true;

    const selectedLevels = classes
      .filter((c: IDogClassElement) => c.show_element === elementLabel && c.level && c.level !== "None")
      .map((c: IDogClassElement) => c.level as string);

    return offeredLevels.some(level => !selectedLevels.includes(level));
  };

  const duplicateRow = (rIndex: number) => {
    if (form) {
      let _form:IEventRegisterRowElement[] = JSON.parse(JSON.stringify(form));

      let _row:IEventRegisterRowElement = JSON.parse(JSON.stringify(form[rIndex]));
      _row.allowDuplicates = false;
      _row.duplicate = true;

      let id:string = createId();

      for (let i in _row.inputs) {
        _row.inputs[i].id = id;
      }

      _form.splice(rIndex + 1, 0, JSON.parse(JSON.stringify(_row)));

      setForm(_form);
    }
  }

  const removeRow = (rIndex: number) => {
    if (form) {
      let _form:IEventRegisterRowElement[] = JSON.parse(JSON.stringify(form));
      let _classes = JSON.parse(JSON.stringify(classes));

      for (let i in _form[rIndex].inputs) {
        let _class = _classes.filter((c: IDogClassElement) => c.id === _form[rIndex].inputs[i].id)[0];

        if (_class) {
          _classes.splice(_classes.indexOf(_class), 1);
        }
      }

      setClasses(_classes);

      _form.splice(rIndex, 1);
      setForm(_form);
    }
  }

  const getJudge = (showElement: string) => {
    return props.show.show_elements.filter((e:IShowElement) => e.show_element === showElement)[0]?.judge_name || undefined;
  }

  // C-WAGS registration: judges assigned to this checkbox row.
  // Scent / Games key on show_element ("Colors"); Obedience / Rally key on level ("Starter").
  // When judges differ by level/section, list each unique name.
  const getCwagsElementJudges = (input: IEventRegisterInputElement): string[] => {
    if (props.event?.sanctioning_club !== Constants.sanctioning_club.C_WAGS) {
      return [];
    }

    const isLevelBased =
      props.show.show_type === Constants.show_type.obedience ||
      props.show.show_type === Constants.show_type.rally_obedience;

    const matching = (props.show.show_elements || []).filter((e: IShowElement) =>
      isLevelBased ? e.level === input.label : e.show_element === input.label
    );

    return [...new Set(
      matching
        .map((e) => (e.judge_name || "").trim())
        .filter((name) => name.length > 0 && name !== "N/A")
    )];
  };

  const isAHBAHerdingShow = props.event?.sanctioning_club === Constants.sanctioning_club.AHBA &&
    props.show.show_type === Constants.show_type.herding;

  // AHBA test rows (JHD / HCT) have a single stock dropdown labelled with the test.
  // Entries carry the stock as both level and section; older entries only have it as the level.
  const isAHBATestInput = (input: IEventRegisterInputElement) =>
    isAHBAHerdingShow && input.elementType === "section" && input.label === input.showElement;

  const getAHBATestStock = (dogClass: IDogClassElement) =>
    dogClass.section && dogClass.section !== "None" ? dogClass.section : dogClass.level;

  const getAHBAElement = (input: IEventRegisterInputElement) =>
    input.elementType === "section" ? input.showElement : input.label;

  const isAHBAElementOffered = (input: IEventRegisterInputElement) =>
    props.show.show_elements.some((se: IShowElement) => se.show_element === getAHBAElement(input));

  const getAHBASelectedLevel = (input: IEventRegisterInputElement): string | undefined =>
    classes.find((c) => c.id === input.id)?.level;

  const getAHBAOfferedValues = (input: IEventRegisterInputElement): Set<string> => {
    const forElement = props.show.show_elements.filter((se: IShowElement) => se.show_element === getAHBAElement(input));

    if (input.elementType === "level") {
      return new Set(forElement.map((se) => se.level).filter((v): v is string => !!v));
    }

    if (isAHBATestInput(input)) {
      return new Set(forElement.map((se) => se.section).filter((v): v is string => !!v));
    }

    const level = getAHBASelectedLevel(input);
    const atLevel = level && level !== "None" ? forElement.filter((se) => se.level === level) : forElement;
    return new Set(atLevel.map((se) => se.section).filter((v): v is string => !!v));
  };

  const getCheckboxTitle = (input: IEventRegisterInputElement): React.ReactNode => {
    const title = input.displayLabel ?? input.label;
    const judges = getCwagsElementJudges(input);
    if (judges.length === 0) {
      return title;
    }
    return <>{title} (<strong>{judges.join(", ")}</strong>)</>;
  };

  const getLabel = (input: IEventRegisterInputElement) => {
    if (nestedElementSetupShows.includes(props.show.show_type)) {
      if (isAHBATestInput(input)) {
        if (!isAHBAElementOffered(input)) {
          return input.label + " (Unavailable)";
        }
        const stock = getValue(input).value;
        const judgeName = props.show.show_elements.find((se: IShowElement) =>
          se.show_element === input.showElement && se.section === stock
        )?.judge_name;
        return judgeName ? `${input.label}, judged by ${judgeName}` : input.label;
      }

      if (!props.show.show_elements.find((e: IShowElement) => e.show_element === input.label) && input.elementType === "level") {
        return input.label + " (Unavailable)";
      }

      if (input.elementType !== "section") {
        return input.label;
      }

      const levelSelect = document.getElementById(`${input.id}-level`);
      const sectionSelect = document.getElementById(`${input.id}-section`);

      const sectionBaseLabel = props.show.show_type === Constants.show_type.herding ? "Stock" : input.label;

      if (levelSelect && sectionSelect && levelSelect instanceof HTMLSelectElement && sectionSelect instanceof HTMLSelectElement) {

        const showElement = props.show.show_elements.find((se) => {
          return se.section === sectionSelect.value && se.level === levelSelect.value && se.show_element === input.showElement;
        });
        const judgeName = showElement?.judge_name;

        return [sectionBaseLabel, judgeName && ", judged by ", judgeName ?? ""].join("");
      }

      // DOM not yet mounted — return the base label so we never fall through
      // to the non-nested path where it would be marked "(Unavailable)".
      return sectionBaseLabel;
    }

    let label = getJudge(input.label) !== undefined && input.label !== "Section" ? input.label + ", judged by " + getJudge(input.label) : input.label;

    // Check for BHA Barn Hunt and On Leash Run Through - match by level instead of show_element
    const isLevelBasedAvailable = (props.show.show_type === Constants.show_type.barn_hunt || 
                                    props.show.show_type === Constants.show_type.on_leash_run_through) && 
                                   props.show.show_elements.some((e: IShowElement) => e.level === input.label);
    
    // For AKC Conformation Non-Licensed Classes (e.g., JS - Novice, JS - Open, etc.), match by level
    const isNonLicensedLevel = props.show.show_type === Constants.show_type.conformation && 
                                props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
                                props.show.show_elements.some((e: IShowElement) => e.show_element === 'Non-Licensed Classes' && e.level === input.label);

    if (props.show.show_elements.filter((e: IShowElement) => { return e.show_element === input.label; }).length === 0 && 
        !isLevelBasedAvailable && 
        !isNonLicensedLevel &&
        input.label !== "Section") {
      label = input.label + " (Unavailable)";
    }

    return label;
  }

  const hasExtraElements = () => {
    let total:number = 0;

    props.classes.forEach(c => {
      if (c.show_element !== "None") {
        total++;

      }
    });

    if (elementCount && total < elementCount) {
      return true;
    }

    return false;
  }

  const isDropdownDisabled = (input: IEventRegisterInputElement) => {
    if (props.disabled) {
      return true;
    }
    
    if (props.overrideDisable) {
      return false;
    }

    if (isAHBAHerdingShow) {
      if (!isAHBAElementOffered(input)) {
        return true;
      }
      // Competitive stock depends on the level, so it waits until one is picked
      if (input.elementType === "section" && !isAHBATestInput(input)) {
        const level = getAHBASelectedLevel(input);
        return !level || level === "None";
      }
      return false;
    }

    // Special handling for CAT dropdowns - only enabled after timeslot is selected
    if (props.show.show_type === Constants.show_type.cat && input.id === "plan_to_earn_title") {
      return classes.length === 0; // Disabled until a timeslot (class) is selected
    }

    let hasClass = classes.filter((c => c.show_element === input.label)).length > 0;

    if ((props.edit || props.moveUp) && !hasClass && input.label === "Section") {
      let hasId = classes.filter((c => c.id === input.id)).length > 0;

      if (hasId) {
        return false;
      } else {
        // Check for BHA Barn Hunt and On Leash Run Through - match by level instead of show_element
        const hasLevelMatch = (props.show.show_type === Constants.show_type.barn_hunt || 
                               props.show.show_type === Constants.show_type.on_leash_run_through) && 
                              props.show.show_elements.some((e: IShowElement) => e.level === input.label);
        
        // For AKC Conformation Non-Licensed Classes (e.g., JS - Novice, JS - Open, etc.), match by level
        const isNonLicensedLevel = props.show.show_type === Constants.show_type.conformation && 
                                    props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
                                    props.show.show_elements.some((e: IShowElement) => e.show_element === 'Non-Licensed Classes' && e.level === input.label);
        
        if (props.edit && hasExtraElements() && (props.show.show_elements.filter((e: IShowElement) => { return e.show_element === input.label; }).length > 0 || hasLevelMatch || isNonLicensedLevel)) {
          return false;
        } else {
          return true;
        }
        
      }
    } else if ((props.edit || props.moveUp) && !hasClass) {
      // Check for BHA Barn Hunt and On Leash Run Through - match by level instead of show_element
      const hasLevelMatch = (props.show.show_type === Constants.show_type.barn_hunt || 
                             props.show.show_type === Constants.show_type.on_leash_run_through) && 
                            props.show.show_elements.some((e: IShowElement) => e.level === input.label);
      
      // For AKC Conformation Non-Licensed Classes (e.g., JS - Novice, JS - Open, etc.), match by level
      const isNonLicensedLevel = props.show.show_type === Constants.show_type.conformation && 
                                  props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
                                  props.show.show_elements.some((e: IShowElement) => e.show_element === 'Non-Licensed Classes' && e.level === input.label);
      
      if (props.edit && hasExtraElements() && (props.show.show_elements.filter((e: IShowElement) => { return e.show_element === input.label; }).length > 0 || hasLevelMatch || isNonLicensedLevel)) {
        return false;
      } else {
        let hasConfOrAlt = classes.filter((c => c.show_element === Constants.dog_class_element.conformation || c.show_element === Constants.dog_class_element.conformation_altered)).length > 0;

        if (hasConfOrAlt && (input.label === Constants.dog_class_element.conformation || input.label === Constants.dog_class_element.conformation_altered)) {
          return false;
        }

        return true;
      }
    } else if (input.label === "Section" && classes.filter((c: IDogClassElement) => c.id === input.id).length > 0) {
      return false;
    } else if (props.show.show_elements.filter((e: IShowElement) => { return e.show_element === input.label; }).length === 0) {
      // Special case for ABI Country Rat Race, BHA Barn Hunt, and On Leash Run Through - check if input.label matches any level in show_elements
      if ((props.show.show_type === Constants.show_type.country_rat_race || 
           props.show.show_type === Constants.show_type.barn_hunt ||
           props.show.show_type === Constants.show_type.on_leash_run_through) && 
          props.show.show_elements.some((e: IShowElement) => e.level === input.label)) {
        return false;
      }
      
      // For AKC Conformation Non-Licensed Classes (e.g., JS - Novice, JS - Open, etc.), match by level
      if (props.show.show_type === Constants.show_type.conformation && 
          props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
          props.show.show_elements.some((e: IShowElement) => e.show_element === 'Non-Licensed Classes' && e.level === input.label)) {
        return false;
      }
      
      return true;
    }

    return false;
  }

  const isCheckboxDisabled = (option: string, label: string) => {
    if (props.disabled) {
      return true;
    }

    if (props.overrideDisable) {
      return false;
    }

    // Special handling for CAT checkboxes - only enabled after timeslot is selected
    if (props.show.show_type === Constants.show_type.cat && label === "Is your dog Brachycephalic?") {
      return classes.length === 0; // Disabled until a timeslot (class) is selected
    }

    // Get current user to check if they're a kennel club account
    const currentUser = helperService.getUser();

    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.show_type === Constants.show_type.conformation) {
      const specialClasses = [
        Constants.dog_class_element_level.akc_brood_bitch,
        Constants.dog_class_element_level.akc_stud_dog,
      ];

      if (specialClasses.includes(option) && props.dog.sex !== (option === Constants.dog_class_element_level.akc_brood_bitch ? Constants.dog_sex.female : Constants.dog_sex.male)) {
        return true;
      }
    }

    let conformationSingleSelect: string[] = [
      Constants.dog_class_element_level.abi_6_9_months,
      Constants.dog_class_element_level.abi_9_12_months,
      Constants.dog_class_element_level.abi_12_18_months,
      Constants.dog_class_element_level.abi_bred_by_exibitor,
      Constants.dog_class_element_level.abi_open,
      //Constants.dog_class_element_level.abi_best_of_breed,
      Constants.dog_class_element_level.abi_jr_champion,
      Constants.dog_class_element_level.abi_champion,
      Constants.dog_class_element_level.abi_master_champion,
      Constants.dog_class_element_level.abi_grand_champion,
      Constants.dog_class_element_level.abi_supreme_champion,
      Constants.dog_class_element_level.abi_ultimate_champion,
      Constants.dog_class_element_level.abi_royal_champion,
      Constants.dog_class_element_level.abi_legendary_champion,
    ];

    let conformationSingleSelectISWS: string[] = [
      // Including age-specific classes to match ABI behavior (skip age validation)
      Constants.dog_class_element_level.isws_junior_puppy,
      Constants.dog_class_element_level.isws_senior_puppy,
      Constants.dog_class_element_level.isws_1218,
      Constants.dog_class_element_level.isws_novice,
      Constants.dog_class_element_level.isws_canadian_bred,
      Constants.dog_class_element_level.isws_american_bred,
      Constants.dog_class_element_level.isws_bred_by_exibitor,
      Constants.dog_class_element_level.isws_open,
      Constants.dog_class_element_level.isws_veteran,
      Constants.dog_class_element_level.isws_performance_champion,
      Constants.dog_class_element_level.isws_champion,
      Constants.dog_class_element_level.isws_altered_novice,
      Constants.dog_class_element_level.isws_altered_open,
      Constants.dog_class_element_level.isws_altered_champion,
      Constants.dog_class_element_level.isws_brace,
      Constants.dog_class_element_level.isws_stud_dog,
      Constants.dog_class_element_level.isws_brood_bitch,
    ];

    // For AKC Conformation, bypass age validation for junior showmanship classes (all variations)
    const juniorShowmanshipLevels = [
      Constants.dog_class_element_level.akc_novice_junior,
      Constants.dog_class_element_level.akc_novice_intermediate,
      Constants.dog_class_element_level.akc_novice_senior,
      Constants.dog_class_element_level.akc_open_junior,
      Constants.dog_class_element_level.akc_open_intermediate,
      Constants.dog_class_element_level.akc_open_senior,
      Constants.dog_class_element_level.akc_master_class,
      Constants.dog_class_element_level.akc_junior_showmanship_novice,
      Constants.dog_class_element_level.akc_junior_showmanship_open,
      Constants.dog_class_element_level.akc_junior_showmanship_master,
    ];
    
    if (props.show.show_type === Constants.show_type.conformation && props.event!.sanctioning_club === Constants.sanctioning_club.AKC && juniorShowmanshipLevels.includes(option)) {
      return false;
    }

    // For AKC Agility Regular classes are not available with 4" jump height
    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.show_type === Constants.show_type.agility) {
      const clubReg = getClubRegistrationInfo();

      if (clubReg?.akc_agility_jump_height === 4) {
        return !option.includes("Preferred");
      }
      if ([24, 26].includes(clubReg?.akc_agility_jump_height ?? 0)) {
        return option.includes("Preferred");
      }
    }

    // AKC Scentwork: disable levels not offered for this specific element at the show.
    // Each element (Container, Interior, etc.) may have a different subset of levels enabled.
    // Backward compat: if the element has a legacy entry without a level, all levels are available.
    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
        props.show.show_type === Constants.show_type.scentwork) {
      const hasLegacyEntry = props.show.show_elements.some(
        (e: IShowElement) => e.show_element === label && !e.level
      );
      if (!hasLegacyEntry) {
        const isLevelOffered = props.show.show_elements.some(
          (e: IShowElement) => e.show_element === label && e.level === option
        );
        if (!isLevelOffered) return true;
      }
      return false; // Bypass age validation for scentwork
    }

    // CKC Scent Detection: a class is selectable only in a component the club offers it in
    // (Scent Detection Trial Rules 6.8.1, 6.8.2). No age gate (6.1.2 is the club's to check).
    if (props.event?.sanctioning_club === Constants.sanctioning_club.CKC &&
        props.show.show_type === Constants.show_type.scent_detection) {
      const alreadySelected = classes.some(
        (c: IDogClassElement) => c.show_element === label && c.level === option
      );
      if (alreadySelected) return false;
      const isOffered = props.show.show_elements.some(
        (e: IShowElement) => e.show_element === label && e.level === option
      );
      return !isOffered;
    }

    // For AKC Fetch, disable levels that aren't configured in the show's show_elements
    if (props.show.show_type === Constants.show_type.fetch) {
      const isLevelOffered = props.show.show_elements?.some(
        (e: IShowElement) => e.level === option
      );
      return !isLevelOffered;
    }

    // For other show types, bypass age validation entirely
    if (props.show.show_type === Constants.show_type.agility ||
      props.show.show_type === Constants.show_type.weight_pull ||
      props.show.show_type === Constants.show_type.scentwork ||
      props.show.show_type === Constants.show_type.dock_jumping ||
      props.show.show_type === Constants.show_type.herding ||
      props.show.show_type === Constants.show_type.pre_trial_herding ||
      props.show.show_type === Constants.show_type.instinct_test_herding ||
      props.show.show_type === Constants.show_type.sniff_n_go ||
      props.show.show_type === Constants.show_type.scent ||
      props.show.show_type === Constants.show_type.cwags_games ||
      (props.show.show_type === Constants.show_type.conformation && props.event?.sanctioning_club === Constants.sanctioning_club.ISWS && conformationSingleSelectISWS.includes(option))) {
      return false;
    }

    // For ABI Conformation, bypass age validation but check if the class is available in show_elements
    if (props.show.show_type === Constants.show_type.conformation && 
        props.event?.sanctioning_club === Constants.sanctioning_club.ABI && 
        conformationSingleSelect.includes(option)) {
      // Check if this option is available in the show
      // Regular conformation classes are available only if level="Conformation" exists
      const hasRegularConformationLevel = props.show.show_elements?.some(se => se.level === "Conformation");
      
      if (hasRegularConformationLevel) {
        return false; // Enable regular conformation classes
      }
      
      // If no regular conformation level, this option should be disabled
      return true;
    }

    // For ABI Conformation special classes, check if they're available in show_elements
    if (props.show.show_type === Constants.show_type.conformation && 
        props.event?.sanctioning_club === Constants.sanctioning_club.ABI) {
      const abiSpecialClasses = [
        Constants.dog_class_element.conformation_altered,
        Constants.dog_class_element_level.abi_baby_puppy,
        Constants.dog_class_element.veteran,
        Constants.dog_class_element.brace,
        Constants.dog_class_element.abi_stud_dog,
        Constants.dog_class_element.abi_brood_bitch,
        Constants.dog_class_element.abi_star,
        Constants.dog_class_element.abi_canadian_bred_bis,
        Constants.dog_class_element.abi_bred_by_bis,
        Constants.dog_class_element.abi_show_of_shows
      ];

      if (abiSpecialClasses.includes(option)) {
        // Check if this special class is available in show_elements
        const isAvailable = props.show.show_elements?.some(se => 
          se.show_element === option || se.level === option
        );
        
        if (!isAvailable) {
          return true; // Disable if not available in show_elements
        }
        
        return false; // Enable if available (bypass age validation)
      }
    }

    // Special validation for puppy sweepstakes - was previously required to have a regular class
    // However, per customer feedback, puppy sweepstakes can be entered independently
    // This validation is now REMOVED to allow standalone puppy sweepstakes entries
    // (The logic below is kept for reference but commented out)
    /*
    const allPuppySweepstakesLevels = [
      Constants.dog_class_element_level.akc_puppy_sweepstakes, // old backward compatibility
      Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
      Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
    ];

    if (allPuppySweepstakesLevels.includes(option)) {
      const regularClassLevels = [
        Constants.dog_class_element_level.akc_4_6_month_puppy,
        Constants.dog_class_element_level.akc_6_9_month_puppy,
        Constants.dog_class_element_level.akc_9_12_month_puppy,
        Constants.dog_class_element_level.akc_12_18_month_puppy,
        Constants.dog_class_element_level.akc_18_24_month_puppy,
        Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
        Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
        Constants.dog_class_element_level.akc_amateur_owner_handler,
        Constants.dog_class_element_level.akc_american_bred,
        Constants.dog_class_element_level.akc_open,
        Constants.dog_class_element_level.akc_best_of_breed,
        Constants.dog_class_element_level.akc_veteran_6_8_years,
        Constants.dog_class_element_level.akc_veteran_8_10_years,
        Constants.dog_class_element_level.akc_veteran_8_11_years,
        Constants.dog_class_element_level.akc_veteran_10_12_years,
        Constants.dog_class_element_level.akc_veteran_11_plus_years,
        Constants.dog_class_element_level.akc_veteran_7_plus_years,
        Constants.dog_class_element_level.akc_veteran_8_plus_years,
        Constants.dog_class_element_level.akc_veteran_13_plus_years,
        Constants.dog_class_element_level.akc_veteran_7_10_years,
        Constants.dog_class_element_level.akc_veteran_7_9_years,
        Constants.dog_class_element_level.akc_veteran_9_plus_years,
        Constants.dog_class_element_level.akc_veteran_12_plus_years,
        Constants.dog_class_element_level.akc_coursing,
      ];

      const hasRegularClass = classes.some(c => regularClassLevels.includes(c.level));
      if (!hasRegularClass) {
        return true; // Disable puppy sweepstakes if no regular class is selected
      }
    }
    */

    // CKC Conformation: a sweepstakes entry must also be entered in a regular class, or in
    // Baby Puppy / Exhibition Only (CKC Conformation Show Rules 12.8.1). Keep the option
    // selectable while it is already checked so the exhibitor can uncheck it.
    if (props.event?.sanctioning_club === Constants.sanctioning_club.CKC &&
        props.show.show_type === Constants.show_type.conformation &&
        label === Constants.dog_class_element.conformation_sweepstakes) {
      const alreadySelected = classes.some((c: IDogClassElement) => c.level === option);
      const qualifyingLevels = [
        Constants.dog_class_element_level.ckc_baby_puppy,
        Constants.dog_class_element_level.ckc_exhibition_only,
      ];
      const hasQualifyingClass = classes.some((c: IDogClassElement) =>
        (c.show_element === Constants.dog_class_element.conformation && c.level && c.level !== "None") ||
        qualifyingLevels.includes(c.level)
      );

      if (!alreadySelected && !hasQualifyingClass) {
        return true;
      }
    }

    // CKC Obedience and Rally Obedience class combinations (CKC Obedience Trial & Rally Obedience Trial
    // Rules & Regulations 2025). An already-checked option stays enabled so the exhibitor can uncheck it.
    const isCkcObedience = props.event?.sanctioning_club === Constants.sanctioning_club.CKC &&
      props.show.show_type === Constants.show_type.obedience &&
      (label === Constants.dog_class_element.performance_obedience || label === Constants.dog_class_element.obedience_unofficial);
    const isCkcRally = props.event?.sanctioning_club === Constants.sanctioning_club.CKC &&
      props.show.show_type === Constants.show_type.rally_obedience &&
      (label === Constants.dog_class_element.performance_rally_obedience || label === Constants.dog_class_element.rally_unofficial);
    if (isCkcObedience || isCkcRally) {
      const alreadySelected = classes.some((c: IDogClassElement) => c.level === option);
      if (!alreadySelected) {
        const selected = (level: string) => classes.some((c: IDogClassElement) => c.level === level);
        const exhibitionOnly = Constants.dog_class_element_level.ckc_exhibition_only;
        const regularLevels = isCkcRally ? CKCRallyObedience.regularLevels : CKCObedience.regularLevels;
        const unofficialLevels = isCkcRally ? CKCRallyObedience.unofficialLevels : CKCObedience.unofficialLevels;
        const hasRegularClass = regularLevels.some(selected);
        const hasOfficialClass = hasRegularClass || selected(exhibitionOnly);

        // A and B divisions of the same class are mutually exclusive at one trial
        // (Obedience 10.2.5, 12.1.2, 13.2.4; Rally 9.1.2, 11.1.2, 12.1.2)
        const exclusivePairs: [string, string][] = isCkcRally
          ? [
              [Constants.dog_class_element_level.novice_a, Constants.dog_class_element_level.novice_b],
              [Constants.dog_class_element_level.advanced_a, Constants.dog_class_element_level.advanced_b],
              [Constants.dog_class_element_level.excellent_a, Constants.dog_class_element_level.excellent_b],
            ]
          : [
              [Constants.dog_class_element_level.novice_a, Constants.dog_class_element_level.novice_b],
              [Constants.dog_class_element_level.open_a, Constants.dog_class_element_level.open_b],
              [Constants.dog_class_element_level.utility_a, Constants.dog_class_element_level.utility_b],
            ];
        if (exclusivePairs.some(([a, b]) => (option === a && selected(b)) || (option === b && selected(a)))) {
          return true;
        }

        // Exhibition Only cannot be combined with any regular class (Obedience 15.1.1, Rally 18.1.1)
        if (option === exhibitionOnly && hasRegularClass) {
          return true;
        }
        if (regularLevels.includes(option) && selected(exhibitionOnly)) {
          return true;
        }

        // An unofficial class requires an official class or Exhibition Only (Obedience 16.1.1, Rally 19.1.2)
        if (unofficialLevels.includes(option) && !hasOfficialClass) {
          return true;
        }

        // Obedience Veterans: seven years or older on the day of the trial (16.5.1)
        if (isCkcObedience && option === Constants.dog_class_element_level.veteran) {
          const effectiveDob = existingChanges?.dob?.new || props.dog.dob;
          if (effectiveDob && props.show.show_date && moment(props.show.show_date).diff(effectiveDob, 'months') < 84) {
            return true;
          }
        }
      }
    }

    let checkboxAgeRestriction = yearList.filter((item: any) => { return item.level === option; });

    if (checkboxAgeRestriction.length > 0) {
      // Use proposed DOB if it exists for this event, otherwise use the dog's current DOB
      const effectiveDob = existingChanges?.dob?.new || props.dog.dob;
      
      if (!effectiveDob) {
        // If no DOB is available, cannot validate age - allow the option
        return false;
      }
      
      // CRITICAL: Always use show_date for age calculation, not current date
      // This ensures age eligibility is checked against the show date, not registration date
      const referenceDate = props.show.show_date;
      
      if (!referenceDate) {
        // If show_date is not set, fall back to current date (shouldn't happen in production)
        console.warn('[FormBuilder] show_date not set for age validation, using current date as fallback');
      }
      
      const dobInMonths = moment(referenceDate || new Date()).diff(effectiveDob, 'months');

      // Age must be within [min, max] range (both inclusive on the month boundary)
      // Example: For "12-18 months" class, a dog that is exactly 18 months old is still eligible
      if ((checkboxAgeRestriction[0].min !== -1 && dobInMonths < checkboxAgeRestriction[0].min) ||
          (checkboxAgeRestriction[0].max !== -1 && dobInMonths > checkboxAgeRestriction[0].max)) {
        return true;
      }
    }

    // If formbuilder is in edit mode, and the level is free, don't disable it
    if (props.edit && props.show.show_elements.filter((e:IShowElement) => (option === e.level && e.fee === 0)).length > 0) {
      return false;
    // If formbuilder is in edit mode, and the option isn't already selected, disable it
    } else if (props.edit && props.classes.filter((c) => { return option === c.level; }).length === 0) {

      // All users (including club managers) are restricted to the same number of entries
      // they started with when the modal opened. They can swap (remove one, add one) but
      // cannot net-add entries — this prevents exceeding the show's entry limit.
      if (props.edit && elementCount && props.classes.length < elementCount) {
        // User has a "credit" from removing a class — check if the option is offered at the show
        if (props.show.show_elements.filter((e:IShowElement) => (e.show_element === option || e.level === option)).length === 0) {
          // Backward compat: legacy show_elements without per-level config
          if (props.show.show_elements.some((e:IShowElement) => e.show_element === label && !e.level)) {
            return false;
          }
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    // If the formbuilder is NOT in edit mode and the show element is available at the show
    } else if (props.show.show_elements.filter((e:IShowElement) => (e.show_element === option || e.level === option)).length === 0) {
      // Backward compat: if the parent element has a legacy (no-level) entry in show_elements (old format
      // before per-level configuration), treat all level options as available.
      if (props.show.show_elements.some((e:IShowElement) => e.show_element === label && !e.level)) {
        // fall through — don't disable
      } else {
      // For UKC Conformation, let the UKC-specific logic below handle Total Dog for club managers.
      const isUKCConformation = props.show.show_type === Constants.show_type.conformation && props.event?.sanctioning_club === Constants.sanctioning_club.UKC;
      const isTotalDog = option === Constants.dog_class_element_level.total_dog;
      const isClubManagerContext = props.isClubManager || currentUser.user_type === Constants.user_type.club_manager;

      // Skip this check for Total Dog when club manager — let UKC logic below handle it
      if (isUKCConformation && isTotalDog && isClubManagerContext) {
        // Don't return here, let it fall through to UKC logic below
      } else {
      return true;
      }
      }
    }

    // NOTE: https://app.clickup.com/t/14140964/ED-887
    // Skip non-licensed class restrictions for kennel club accounts
    if (props.show.show_type === Constants.show_type.conformation && props.event?.sanctioning_club === Constants.sanctioning_club.AKC && !classes.length && currentUser.user_type !== Constants.user_type.club_manager) {
      const nonL = ShowTypes[Constants.sanctioning_club.AKC][Constants.show_type.conformation][Constants.dog_class_element.conformation_non_licensed];

      // Allow veteran sweepstakes, dual-title sweepstakes, puppy sweepstakes, and sled dog to be available independently
      const independentSweepstakes = [
        Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus,
        Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y,
        Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18,
        Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24,
        Constants.dog_class_element_level.akc_sled_dog,
        Constants.dog_class_element_level.akc_hunting_class,
        Constants.dog_class_element_level.akc_field_trial_class,
        Constants.dog_class_element_level.akc_teams,
        Constants.dog_class_element_level.akc_parade_of_titleholders,
        Constants.dog_class_element_level.akc_best_head,
        Constants.dog_class_element_level.akc_best_movement,
        Constants.dog_class_element_level.akc_best_field,
      ];

      // Coursing is now a regular conformation class, not a non-licensed element
      if (option === Constants.dog_class_element_level.akc_coursing || independentSweepstakes.includes(option)) {
        return false; // Don't disable these when no classes are selected
      }

      return nonL.some((el: string) => el === option);
    }

    // NOTE: https://app.clickup.com/t/14140964/ED-962
    // Removed: PC core-class mutual exclusion.
    // UKC rule is "max 2 PC runs per day" — multiple levels are allowed in the same trial
    // as long as the per-day total does not exceed 2. That limit is enforced in the cart.

    // NOTE: https://app.clickup.com/t/14140964/ED-963
    // Skip non-licensed class restrictions for kennel club accounts
    if (props.show.show_type === Constants.show_type.conformation && props.event?.sanctioning_club === Constants.sanctioning_club.UKC) {
      const coreElements = [
        Constants.dog_class_element.conformation,
        Constants.dog_class_element.conformation_altered,
        Constants.dog_class_element.junior_showmanship,
      ];

      // Classes that require a regular conformation class to be selected first
      const restrictedOptLevels = [
        Constants.dog_class_element_level.total_dog,
        Constants.dog_class_element_level.total_junior,
      ];

      // Novice Puppy and Veteran are exempt from requiring a regular class - only age validation applies
      if (option === Constants.dog_class_element_level.novice_puppy || option === Constants.dog_class_element_level.veteran) {
        return false; // Don't disable, let age validation handle it
      }

      // Total Dog / Total Junior only exist if the club opted into offering them on the
      // Show Type tab - never selectable (by anyone, including club managers) otherwise.
      if (restrictedOptLevels.includes(option)) {
        const isOfferedAtShow = props.show.show_elements.some(
          (e: IShowElement) => e.show_element === option || e.level === option
        );
        if (!isOfferedAtShow) {
          return true;
        }
      }

      // Club managers (on-behalf-of registrations) can always select Total Dog/Total Junior
      // once the show offers them - they just skip the "core element selected first" requirement below.
      // This includes both user_type=club_manager AND owners with admin capacity for the club
      // Check both props.isClubManager (from EventRegister) and currentUser.user_type (from helper service)
      const isClubManagerContext = props.isClubManager || currentUser.user_type === Constants.user_type.club_manager;
      
      if (isClubManagerContext) {
        return false; // Never disable for club managers or admins once the show offers the class
      }

      // For regular users, apply restrictions
      if (!classes.length) {
        return restrictedOptLevels.includes(option);
      }

      const hasCoreElement = classes.some(cl => coreElements.includes(cl.show_element));
      const isRestrictedLevel = restrictedOptLevels.includes(option);

      // Special case: Allow Total Junior if Junior Showmanship is selected (even without regular Conformation)
      if (option === Constants.dog_class_element_level.total_junior) {
        const hasJuniorShowmanship = classes.some(cl => cl.show_element === Constants.dog_class_element.junior_showmanship);
        if (hasJuniorShowmanship) {
          return false;
        }
      }

      return !hasCoreElement && isRestrictedLevel;
    }

    // Multi-entry validation - disable conflicting classes
    if (props.event && props.show) {
      // If any currently-selected class in this show element has disablesAll=true
      // (e.g. URX or RMX), grey out every other option so the user can't mix
      // combo entries with individual classes.
      const hasDisablesAllSelected = classes.some(
        (c: IDogClassElement) =>
          c.show_element === label &&
          MultiEntryMappingService.isDisablesAll(
            c.level,
            props.event!.sanctioning_club,
            props.show!.show_type
          )
      );

      if (hasDisablesAllSelected) {
        // Keep only the currently-selected multi-entry itself enabled
        const isCurrentlySelected = classes.some(
          (c: IDogClassElement) => c.show_element === label && c.level === option
        );
        if (!isCurrentlySelected) {
          return true;
        }
      }

      const disabledClasses = MultiEntryMappingService.getDisabledClasses(
        classes, 
        props.event.sanctioning_club, 
        props.show.show_type
      );
      
      if (disabledClasses.includes(option)) {
        return true;
      }

      // Also check if selecting this multi-entry would conflict with existing registrations
      const testClass: IDogClassElement = {
        id: 'test',
        show_element: label,
        level: option
      };

      const validation = MultiEntryMappingService.validateClassSelection(
        classes,
        testClass,
        props.event.sanctioning_club,
        props.show.show_type,
        props.classes as any // Pass existing registered classes
      );

      if (!validation.isValid) {
        return true;
      }
    }

    return false;
  }

  const ageRestrictedElements:string[] = [
    Constants.dog_class_element.conformation,
    Constants.dog_class_element.conformation_altered
  ];

  const ageRestrictOptions = (options: IOption[]) => {
    // Use proposed DOB if it exists for this event, otherwise use the dog's current DOB
    const effectiveDob = existingChanges?.dob?.new || props.dog.dob;
    const referenceDate = props.show.show_date || new Date();
    const dobInMonths = moment(referenceDate).diff(effectiveDob, 'months');

    let filteredOptions:IOption[] = JSON.parse(JSON.stringify(options));

    for (let i in filteredOptions) {
      for (let y in yearList) {
        if (filteredOptions[i].value === yearList[y].level) {
          filteredOptions[i].disabled = true;

          if (
            (dobInMonths >= yearList[y].min || yearList[y].min === -1) && 
            (dobInMonths < yearList[y].max || yearList[y].max === -1)
          ) {
            filteredOptions[i].disabled = false;
          }

          break;
        }
      }
    }

    return filteredOptions;
  }

  const dupCheckOptions = (input: any) => {
     let filteredOptions:IOption[] = JSON.parse(JSON.stringify(input.options));

     const isUKCNosework = props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
       props.show.show_type === Constants.show_type.nosework;

     const isABIUrbanRatRace = props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
       props.show.show_type === Constants.show_type.urban_rat_race;
     const isUrbanRatRaceLevelDropdown = isABIUrbanRatRace && input.elementType === "level";
     const isUrbanRatRaceSectionDropdown = isABIUrbanRatRace && input.elementType === "section";

     // ABI Scent Dog Games: filter section dropdown to exclude sections already used for this game level
     const isABIScentDogGames = props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
       props.show.show_type === Constants.show_type.scent_dog_games;
     if (isABIScentDogGames && input.label === "Section" && input.sdgLevelConstant) {
       const usedSections = (classes as any[])
         .filter(c => c.show_element === Constants.show_type.scent_dog_games && c.level === input.sdgLevelConstant)
         .map(c => c.section)
         .filter(Boolean) as string[];

       return filteredOptions.map((opt: IOption) => ({
         ...opt,
         disabled: opt.value !== "None" && usedSections.includes(opt.value),
       }));
     }

     const isABILostAndFound = props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
       props.show.show_type === Constants.show_type.lost_and_found;
     const isLostAndFoundLevelDropdown = isABILostAndFound && input.elementType === "level";
     const isLostAndFoundSectionDropdown = isABILostAndFound && input.elementType === "section";

     const isAKCHerding = props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
       props.show.show_type === Constants.show_type.herding;
     const isAkcHerdingStockOnly = props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
       [Constants.show_type.pre_trial_herding, Constants.show_type.instinct_test_herding].includes(props.show.show_type);
     const isHerdingLevelDropdown = isAKCHerding && input.elementType === "level";
     const isHerdingStockDropdown = isAKCHerding && input.elementType === "section";
     const isStockOnlyLevelDropdown = isAkcHerdingStockOnly && input.elementType === "level";

     const isAHBAHerding = props.event?.sanctioning_club === Constants.sanctioning_club.AHBA &&
       props.show.show_type === Constants.show_type.herding;
     const isAHBAHerdingLevelDropdown = isAHBAHerding && input.elementType === "level";
     const isAHBAHerdingStockDropdown = isAHBAHerding && input.elementType === "section";

    // ABI Urban Rat Race: filter levels and sections by show_elements, and prevent duplicate combos
    if (isUrbanRatRaceLevelDropdown) {
      for (const option of filteredOptions) {
        if (option.value === "None") {
          option.disabled = false;
          continue;
        }
        const isLevelOffered = props.show.show_elements.some((se: IShowElement) =>
          se.show_element === input.label && se.level === option.value
        );
        if (!isLevelOffered) {
          option.disabled = true;
          continue;
        }
        // Disable if any other row for the same element has already selected this level
        const isAlreadySelected = props.classes.some((c: any) =>
          c.id !== input.id &&
          c.show_element === input.label &&
          c.level === option.value
        );
        option.disabled = isAlreadySelected;
      }
    } else if (isUrbanRatRaceSectionDropdown) {
      const levelElement = document.getElementById(`${input.id}-level`);
      const selectedLevel = levelElement && levelElement instanceof HTMLSelectElement
        ? levelElement.value
        : props.classes.find((c: any) => c.id === input.id)?.level;

      for (const option of filteredOptions) {
        if (option.value === "None") {
          option.disabled = false;
          continue;
        }
        if (!selectedLevel || selectedLevel === "None") {
          option.disabled = true;
          continue;
        }
        const isSectionOffered = props.show.show_elements.some((se: IShowElement) =>
          se.show_element === input.showElement &&
          se.level === selectedLevel &&
          se.section === option.value
        );
        // Also disable if another row already has this exact element+level+section combo
        const isSectionAlreadyTaken = props.classes.some((c: any) =>
          c.id !== input.id &&
          c.show_element === input.showElement &&
          c.level === selectedLevel &&
          c.section === option.value
        );
        option.disabled = !isSectionOffered || isSectionAlreadyTaken;
      }
    } else if (isLostAndFoundLevelDropdown) {
       for (const option of filteredOptions) {
         if (option.value === "None") {
           option.disabled = false;
           continue;
         }
         const isLevelOffered = props.show.show_elements.some((se: IShowElement) =>
           se.show_element === input.label && se.level === option.value
         );
         option.disabled = !isLevelOffered;
       }
     } else if (isLostAndFoundSectionDropdown) {
       const lfLevelElement = document.getElementById(`${input.id}-level`);
       const lfSelectedLevel = lfLevelElement && lfLevelElement instanceof HTMLSelectElement
         ? lfLevelElement.value
         : props.classes.find((c: any) => c.id === input.id)?.level;

       for (const option of filteredOptions) {
         if (option.value === "None") {
           option.disabled = false;
           continue;
         }
         if (!lfSelectedLevel || lfSelectedLevel === "None") {
           option.disabled = true;
           continue;
         }
         const isSectionOffered = props.show.show_elements.some((se: IShowElement) =>
           se.show_element === input.showElement &&
           se.level === lfSelectedLevel &&
           se.section === option.value
         );
         option.disabled = !isSectionOffered;
       }
     } else if (isStockOnlyLevelDropdown) {
       for (const option of filteredOptions) {
         if (option.value === "None") {
           option.disabled = false;
           continue;
         }

         const isStockOfferedAtShow = props.show.show_elements.some((showElement: IShowElement) => (
           showElement.show_element === input.label && showElement.level === option.value
         ));
         const isStockAlreadyUsed = props.classes.some((dogClass: any) => (
           dogClass.id !== input.id &&
           dogClass.show_element === input.label &&
           dogClass.level === option.value
         ));

         option.disabled = !isStockOfferedAtShow || isStockAlreadyUsed;
       }
     } else if (isHerdingStockDropdown) {
       const levelElement = document.getElementById(`${input.id}-level`);
      const selectedLevel = levelElement && levelElement instanceof HTMLSelectElement
        ? levelElement.value
        : props.classes.find((c: any) => c.id === input.id)?.level;

       for (const option of filteredOptions) {
         if (option.value === "None") {
           option.disabled = false;
           continue;
         }

        const isStockAlreadyUsed = props.classes.some((dogClass: any) => (
           dogClass.id !== input.id &&
           dogClass.show_element === input.showElement &&
           dogClass.level === selectedLevel &&
           dogClass.section === option.value
         ));

         option.disabled = isStockAlreadyUsed;
       }
     } else if (isAHBAHerdingLevelDropdown) {
       const offered = getAHBAOfferedValues(input);
       for (const option of filteredOptions) {
         option.disabled = option.value !== "None" && !offered.has(option.value);
       }
     } else if (isAHBAHerdingStockDropdown) {
       const selectedLevel = getAHBASelectedLevel(input);
       const offered = getAHBAOfferedValues(input);

       for (const option of filteredOptions) {
         if (option.value === "None") {
           option.disabled = false;
           continue;
         }

         const isStockAlreadyUsed = props.classes.some((dogClass: any) => (
           dogClass.id !== input.id &&
           dogClass.show_element === input.showElement &&
           (isAHBATestInput(input)
             ? getAHBATestStock(dogClass) === option.value
             : dogClass.level === selectedLevel && dogClass.section === option.value)
         ));

         option.disabled = !offered.has(option.value) || isStockAlreadyUsed;
       }
     } else if (!isHerdingLevelDropdown && !isAHBAHerdingLevelDropdown) {
       for (let i in filteredOptions) {
         for (let c in props.classes) {
           if (props.classes[c].show_element === input.label && props.classes[c].level === filteredOptions[i].value && filteredOptions[i].value !== getValue(input).value) {
             filteredOptions[i].disabled = true;
             break;
           } else {
             filteredOptions[i].disabled = false;
           }
         }
       }
     } else {
       for (const option of filteredOptions) {
         option.disabled = false;
       }
     }

     // UKC Nosework: filter level dropdown options to only levels offered at this show.
     // Applied AFTER the duplicate-check block so it always wins.
     // Backward compat: if show_elements has an entry for this element without a level (old format),
     // all levels are available (no filtering).
     // Only applies to element (level) dropdowns, not the section (A/B) dropdown which uses label "Section".
     if (isUKCNosework && input.label !== "Section") {
       const hasLegacyEntry = props.show.show_elements.some(
         (se: IShowElement) => se.show_element === input.label && !se.level
       );
       if (!hasLegacyEntry) {
         for (const option of filteredOptions) {
           if (option.value === "None") {
             option.disabled = false;
             continue;
           }
           const isLevelOffered = props.show.show_elements.some(
             (se: IShowElement) => se.show_element === input.label && se.level === option.value
           );
           if (!isLevelOffered) {
             option.disabled = true;
           }
         }
       }
     }

     // Oxford Start
     if ((props.event && (props.event.handle === "u35ag9" || props.event.handle === "k0nya5" || props.event.handle === "odkn86")) && input.type === "dropdown" && input.label === Constants.dog_class_element.nosework_handler_descrimination) {
      for (let i in filteredOptions) {
        if (filteredOptions[i].id === Constants.dog_class_element_level.master) {
          filteredOptions[i].disabled = true;
        }
      }
     }
     // Oxford End



     // SEAEDA 2025 Start - disable Elite level for all elements
     if (props.event && props.event.handle === "dtrrcz" && input.type === "dropdown" && (
       input.label === Constants.dog_class_element.nosework_container ||
       input.label === Constants.dog_class_element.nosework_exterior ||
       input.label === Constants.dog_class_element.nosework_interior ||
       input.label === Constants.dog_class_element.nosework_vehicle ||
       input.label === Constants.dog_class_element.nosework_handler_descrimination
     )) {
       for (let i in filteredOptions) {
         if (filteredOptions[i].id === Constants.dog_class_element_level.elite) {
           filteredOptions[i].disabled = true;
         }
       }
     }
     // SEAEDA 2025 End

     // phrslz Start - only Novice, Advanced, Superior allowed
     if (props.event && props.event.handle === "phrslz" && input.type === "dropdown" && (
       input.label === Constants.dog_class_element.nosework_container ||
       input.label === Constants.dog_class_element.nosework_exterior ||
       input.label === Constants.dog_class_element.nosework_interior ||
       input.label === Constants.dog_class_element.nosework_vehicle ||
       input.label === Constants.dog_class_element.nosework_handler_descrimination
     )) {
       for (let i in filteredOptions) {
         if (
           filteredOptions[i].id === Constants.dog_class_element_level.master ||
           filteredOptions[i].id === Constants.dog_class_element_level.elite
         ) {
           filteredOptions[i].disabled = true;
         }
       }
     }
     // phrslz End

     return filteredOptions;
  }

  const isElementAndAvailable = (input: IEventRegisterInputElement): boolean => {
    // For CAT, always show the extra data checkboxes
    if (props.show.show_type === Constants.show_type.cat && 
        (input.id === "plan_to_earn_title" || input.id === "is_brachycephalic")) {
      return true;
    }

    // For ASFA, always show metadata question checkboxes
    if (isAsfaMetadataInput(input.id)) {
      return true;
    }

    // For BHA Barn Hunt, check by level instead of show_element
    if (props.show.show_type === Constants.show_type.barn_hunt) {
      return !!props.show.show_elements.find((se) => se.level === input.label);
    }

    // For Obedience Show N Go and General Rally, show_element in show_elements is the level string (levels-as-keys pattern)
    if (props.show.show_type === Constants.show_type.obedience_show_n_go ||
        props.show.show_type === Constants.show_type.general_rally) {
      return !!props.show.show_elements.find((se) => se.show_element === input.label);
    }

    // For C-WAGS Obedience, check by level instead of show_element
    if (props.event &&
        props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
        props.show.show_type === Constants.show_type.obedience) {
      return !!props.show.show_elements.find((se) => se.level === input.label);
    }

    // For C-WAGS Rally, check by level instead of show_element
    if (props.event &&
        props.event.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
        props.show.show_type === Constants.show_type.rally_obedience) {
      return !!props.show.show_elements.find((se) => se.level === input.label);
    }

    // For ABI Conformation, show_element is "Conformation" and levels are the individual options
    // Check if ANY element with show_element matching input.label exists
    if (props.event &&
        props.event.sanctioning_club === Constants.sanctioning_club.ABI &&
        props.show.show_type === Constants.show_type.conformation &&
        input.label === Constants.dog_class_element.conformation) {
      return props.show.show_elements.some((se) => se.show_element === Constants.show_type.conformation);
    }

    // For AKC Conformation "Special Classes", check if any of the special class levels exist
    // These are stored as "Non-Licensed Classes" in the database but displayed separately
    if (props.show.show_type === Constants.show_type.conformation &&
        input.label === Constants.dog_class_element.conformation_special_classes) {
      return props.show.show_elements.some((se) => 
        se.level === Constants.dog_class_element_level.akc_brace ||
        se.level === Constants.dog_class_element_level.akc_brood_bitch ||
        se.level === Constants.dog_class_element_level.akc_stud_dog
      );
    }

    // For AKC Conformation "Non-Licensed Classes", check if any veteran sweepstakes, puppy sweepstakes, or junior showmanship exist
    if (props.event?.sanctioning_club === Constants.sanctioning_club.AKC &&
        props.show.show_type === Constants.show_type.conformation &&
        input.label === Constants.dog_class_element.conformation_non_licensed) {
      return props.show.show_elements.some((se) => 
        // Puppy Sweepstakes
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_6_9 ||
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_9_12 ||
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_12_18 ||
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_12_15 ||
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_15_18 ||
        se.level === Constants.dog_class_element_level.akc_puppy_sweepstakes_18_24 ||
        // Veteran Sweepstakes
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_7_9 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_7_10 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_8_10 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_8_11 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_9_plus ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_10_12 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_11_plus ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_10_13 ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_10_plus ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_12_plus ||
        se.level === Constants.dog_class_element_level.akc_veteran_sweepstakes_13_plus ||
        // Dual Title Sweepstakes
        se.level === Constants.dog_class_element_level.akc_dual_title_sweepstakes_6m_7y ||
        se.level === Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_plus ||
        se.level === Constants.dog_class_element_level.akc_dual_title_sweepstakes_7y_11y ||
        se.level === Constants.dog_class_element_level.akc_dual_title_sweepstakes_11y_plus ||
        // Other Non-Licensed
        se.level === Constants.dog_class_element_level.akc_futurity ||
        se.level === Constants.dog_class_element_level.akc_sled_dog ||
        se.level === Constants.dog_class_element_level.akc_hunting_class ||
        se.level === Constants.dog_class_element_level.akc_field_trial_class ||
        se.level === Constants.dog_class_element_level.akc_teams ||
        se.level === Constants.dog_class_element_level.akc_parade_of_titleholders ||
        se.level === Constants.dog_class_element_level.akc_best_head ||
        se.level === Constants.dog_class_element_level.akc_best_movement ||
        se.level === Constants.dog_class_element_level.akc_best_field ||
        // Junior Showmanship
        se.level === Constants.dog_class_element_level.akc_novice_junior ||
        se.level === Constants.dog_class_element_level.akc_novice_intermediate ||
        se.level === Constants.dog_class_element_level.akc_novice_senior ||
        se.level === Constants.dog_class_element_level.akc_open_junior ||
        se.level === Constants.dog_class_element_level.akc_open_intermediate ||
        se.level === Constants.dog_class_element_level.akc_open_senior ||
        se.level === Constants.dog_class_element_level.akc_master_class ||
        se.level === Constants.dog_class_element_level.akc_junior_showmanship_novice ||
        se.level === Constants.dog_class_element_level.akc_junior_showmanship_open ||
        se.level === Constants.dog_class_element_level.akc_junior_showmanship_master
      );
    }

    // For UKC Conformation "Non-Licensed Classes", always return true and let getForm() filter the options
    // UKC stores non-licensed classes as individual show_elements (e.g., "Veteran", "Novice Puppy")
    // NOT as a grouped "Non-Licensed Classes" show_element
    if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        props.show.show_type === Constants.show_type.conformation &&
        input.label === Constants.dog_class_element.conformation_non_licensed) {
      return true; // Let Conformation.getForm() filter which specific options to show
    }

    // For UKC Precision Coursing "Non-Licensed Classes" (Fun Run, Puppy Dash), always return true
    // These are stored with show_element: "Precision Coursing" and level: "Fun Run"/"Puppy Dash"
    // so the default show_element check would fail. UKCPrecisionCoursing.getForm() handles filtering.
    if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        props.show.show_type === Constants.show_type.precision_coursing &&
        input.label === Constants.dog_class_element.conformation_non_licensed) {
      return props.show.show_elements.some((se) =>
        se.level === Constants.dog_class_element_level.fun_run ||
        se.level === Constants.dog_class_element_level.puppy_dash
      );
    }

    // For UKC and other sanctioning clubs: Use default check
    // Special handling for UKC Agility Games: All UKC Agility elements (including games) are stored with show_element: "Agility"
    if (props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
        props.show.show_type === Constants.show_type.agility &&
        input.label === Constants.dog_class_element.games) {
      // Check if there are any games levels in the show_elements
      // Games levels are: Novice Hurdles, Elite Hurdles, Novice Tunnels, Elite Tunnels, 3 of a Kind
      return props.show.show_elements.some((se) => 
        se.show_element === Constants.dog_class_element.agility &&
        (se.level === Constants.dog_class_element_level.novice_hurdles ||
         se.level === Constants.dog_class_element_level.elite_hurdles ||
         se.level === Constants.dog_class_element_level.novice_tunnels ||
         se.level === Constants.dog_class_element_level.elite_tunnels ||
         se.level === Constants.dog_class_element_level.three_of_a_kind)
      );
    }

    return !!props.show.show_elements.find((se) => se.show_element === input.label);
  }

  const filterDropdownOptions = (input: IEventRegisterInput): IOption[] => {
    // AHBA Herding rows always allow duplicates, so their options are handled in dupCheckOptions
    if (isAHBAHerdingShow) {
      return input.options;
    }

    // only available for nested elements setup (Herding);
    // for old shows set 'elementType', 'showElement' in form configuration
    if (!nestedElementSetupShows.includes(props.show.show_type)) {
      if (props.show.show_type === Constants.show_type.urban_rat_race) {
        const sectionElement = document.getElementById(`${input.id}-section`);
        if (sectionElement && sectionElement instanceof HTMLSelectElement) {

          // find related level dropdown
          const levelElement = document.getElementById(`${input.id}-level`);
          if (levelElement && levelElement instanceof HTMLSelectElement) {

            // Offleashed is only Adventurer or higher and counts towards excellent titles only
            if ([
              Constants.dog_class_element_level.rookie,
              Constants.dog_class_element_level.clever,
            ].includes(levelElement.value)) {
              return input.options.filter(
                (option) => option.value !== Constants.dog_class_section.unleashed
              );
            }

            return input.options
          }
        }

        return input.options
      }

      return input.options;
    }

    // C-WAGS Obedience / Rally: only offer the sections (A / B / TO / JR / FEO) the club actually
    // selected for this level in show setup. The Section dropdown shares its id with the level
    // checkbox in the same row, and that checkbox's label is the level.
    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
      (props.show.show_type === Constants.show_type.obedience || props.show.show_type === Constants.show_type.rally_obedience) &&
      (input.label === "Section" || input.elementType === "section")
    ) {
      const row = form?.find((r: IEventRegisterRowElement) => r.inputs.some((i: IEventRegisterInputElement) => i.id === input.id));
      const rowLevel = row?.inputs.find((i: IEventRegisterInputElement) => i.type === "checkbox")?.label;
      const offeredSections = new Set(
        props.show.show_elements
          .filter((se: IShowElement) => (!rowLevel || se.level === rowLevel) && !!se.section)
          .map((se: IShowElement) => se.section as string)
      );

      // Older shows saved without per-section rows: keep the full list
      if (offeredSections.size === 0) {
        return input.options;
      }

      return input.options.filter((option) => option.value === "None" || offeredSections.has(option.value));
    }

    return input.options.filter((option) => {
      if (option.value === "None") {
        return option;
      }

      return props.show.show_elements.find((se) => {
        if (input.elementType === "level") {
          return se.level === option.value && se.show_element === input.label;
        }

        if (input.elementType === "section") {
          const selectElement = document.getElementById(`${input.id}-level`);

          if (selectElement && selectElement instanceof HTMLSelectElement) {
            return se.section === option.value && se.level === selectElement.value && se.show_element === input.showElement;
          }
        }

        return option;
      });
    })
  }

  const isTimeSlotDisabled = (t: IShowRunTimeSlot, forInstanceId?: string) => {
    const isFull = (t.num_available - t.num_reserved <= 0);
    // A full slot is still selectable when waitlist is enabled — the user is joining
    // the waitlist with a preferred time, not consuming a physical slot.
    if (isFull && props.show.waitlist_enabled) {
      return false;
    }

    // Per-class mode (OSNG / Rally with level-scoped slots):
    // Each selected class ENTRY (not just level — a dog may enter the same
    // level multiple times via the "+" duplicate button) gets its own
    // independent timeslot picker, keyed by that class's unique row id.
    if (forInstanceId !== undefined) {
      const thisEntry = classes.find((c) => c.id === forInstanceId);
      if (!thisEntry || isFull) return true;

      // A dog can't run two entries at the exact same physical time — disable
      // this slot if another one of the dog's entries in this show already holds it.
      const alreadyBookedElsewhere = classes.some((c) =>
        c.id !== forInstanceId && c.level && c.level !== "None" && c.time_slot === t.time_slot
      );
      return alreadyBookedElsewhere;
    }

    // For FastCAT, Fast Dash, CAT, and Fetch, always allow selection if slots are available
    if (props.show.show_type === Constants.show_type.fast_cat || 
        props.show.show_type === Constants.show_type.fast_dash ||
        props.show.show_type === Constants.show_type.cat ||
        props.show.show_type === Constants.show_type.fetch) {
      return isFull;
    }
    
    // For other show types, also check if classes exist
    return isFull || (props.classes.length === 0);
  }

  const getTimeSlotClass = (t: IShowRunTimeSlot, tIndex: number, forInstanceId?: string) => {
    let _class:string = "no-select ";

    const isFull = (t.num_available - t.num_reserved <= 0);

    if (isTimeSlotDisabled(t, forInstanceId)) {
      _class += "disabled";
    } else if (isFull && props.show.waitlist_enabled) {
      // Full but selectable for waitlist — use the waitlist active/inactive style
      let isActive: boolean;
      if (forInstanceId !== undefined) {
        isActive = selectedTimeslotByLevel[forInstanceId] === tIndex;
      } else {
        isActive = (props.show.show_type === Constants.show_type.fast_cat || 
                    props.show.show_type === Constants.show_type.fast_dash || 
                    props.show.show_type === Constants.show_type.cat)
          ? classes.some(c => c.time_slot === t.time_slot)
          : selectedTimeslot === tIndex;
      }
      _class += isActive ? "active" : "waitlist";
    } else {
      let isActive: boolean;
      if (forInstanceId !== undefined) {
        isActive = selectedTimeslotByLevel[forInstanceId] === tIndex;
      } else {
        isActive = (props.show.show_type === Constants.show_type.fast_cat || 
                    props.show.show_type === Constants.show_type.fast_dash || 
                    props.show.show_type === Constants.show_type.cat)
          ? classes.some(c => c.time_slot === t.time_slot)
          : selectedTimeslot === tIndex;
      }
        
      if (isActive) {
        _class += "active";
      }
    }

    return _class;
  }

  const handleTeamDogSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    const teamDogs = getTeamDogs();
    if (teamDogs.length === 3 && !teamDogs.map((d) => d._id).includes(dog._id)) {
      toast.show({
        title: "Show Registration",
        content: "You can pick only 3 teammates for Obedience Team",
        duration: 10000,
        type: "fail"
      })
      return;
    }

    if (!teamDogs.includes(dog)) {
      const dogs = [...teamDogs, dog];
      const payload = { id: "extra_arr", value: dogs.map((d) => d._id).toString() };
      updateClasses(input, payload, undefined, dog._id, Constants.dog_class_element_level.team);
    } else {
      const dogs = teamDogs.filter((d) => d._id !== dog._id);
      const payload = { id: "extra_arr", value: dogs.map((d) => d._id).toString() };
      updateClasses(input, payload, undefined, dog._id, Constants.dog_class_element_level.team);
    }
  }

  const handleBraceDogSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    updateClasses(input, { id: "extra", value: dog._id }, dog._id, undefined, Constants.dog_class_element_level.brace);
  }

  const handleAKCBraceSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    const currentDogs = getAKCBraceDogs();
    
    // Toggle selection: if dog is already selected, deselect it; otherwise select it
    if (currentDogs.some((d) => d._id === dog._id)) {
      // Dog is already selected, deselect it by setting extra to empty
      const payload = { id: "extra", value: "" };
      updateClasses(input, payload, "", undefined, Constants.dog_class_element_level.akc_brace, true);
    } else {
      // Dog is not selected, select it
      updateClasses(input, { id: "extra", value: dog._id }, dog._id, undefined, Constants.dog_class_element_level.akc_brace);
    }
  }

  const handlePairsDogSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    updateClasses(input, { id: "extra", value: dog._id }, dog._id, undefined, Constants.dog_class_element_level.pairs);
  }

  const handleNovicePairsDogSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    updateClasses(input, { id: "extra", value: dog._id }, dog._id, undefined, Constants.dog_class_element_level.novice_pairs);
  }

  const handleAdvancedPairsDogSelect = (dog: IDog, input: IEventRegisterInputElement) => {
    updateClasses(input, { id: "extra", value: dog._id }, dog._id, undefined, Constants.dog_class_element_level.advanced_pairs);
  }

  const getDogListLabel = (dogClass: string) => {
    const labelLevel = classes.find((c: IDogClassElement) => c.level === dogClass);
    return labelLevel?.level === Constants.dog_class_element_level.brace ? "Brace" : "Pairs";
  }

  // https://app.clickup.com/t/14140964/ED-1146
  const getIsChecked = (isSpecialClassesSection: boolean, option: string, inputLabel: string, inputId?: string, inputSection?: string) => {
    // C-WAGS Scent / Games: regular row and FEO row share show_element + level; the section decides
    if (isCwagsFeoShowWideShow) {
      return classes.some((c: IDogClassElement) =>
        c.show_element === inputLabel && c.level === option && sameSectionAsInput(c, { section: inputSection })
      );
    }

    const optionId = props.event?.sanctioning_club === Constants.sanctioning_club.ABI && option === Constants.dog_class_element.conformation_altered && props.edit
      ? Constants.dog_class_element_level.abi_altered
      : option

    // Special handling for CAT extra data checkboxes
    if (props.show.show_type === Constants.show_type.cat) {
      if (inputId === "plan_to_earn_title") {
        return classes.some(c => c.plan_to_earn_title === optionId);
      }
      if (inputId === "is_brachycephalic") {
        return classes.some(c => c.is_brachycephalic === true);
      }
    }

    if (
      props.event?.sanctioning_club === Constants.sanctioning_club.ASFA &&
      props.show.show_type === Constants.show_type.field_trial
    ) {
      if (inputId === "asfa_first_trial_for_hound") {
        const val = classes.find((c) => c.asfa_first_trial_for_hound !== undefined)?.asfa_first_trial_for_hound;
        return optionId === "yes" ? val === true : val === false;
      }
      if (inputId === "asfa_first_time_entry") {
        const val = classes.find((c) => c.asfa_first_time_entry !== undefined)?.asfa_first_time_entry;
        return optionId === "yes" ? val === true : val === false;
      }
      if (inputId === "asfa_info_changed_since_last_trial") {
        const val = classes.find((c) => c.asfa_info_changed_since_last_trial !== undefined)?.asfa_info_changed_since_last_trial;
        return optionId === "yes" ? val === true : val === false;
      }
      if (inputId === "asfa_dismissed_last_6_trials") {
        const val = classes.find((c) => c.asfa_dismissed_last_6_trials !== undefined)?.asfa_dismissed_last_6_trials;
        return optionId === "yes" ? val === true : val === false;
      }
    }

    // For Barn Hunt, the inputLabel is the level (e.g., "Instinct") but show_element is "Barn Hunt"
    // So we need to check by level only, similar to Special Classes
    const isBarnHunt = props.event?.sanctioning_club === Constants.sanctioning_club.BHA &&
                       props.show.show_type === Constants.show_type.barn_hunt;

    // For C-WAGS Obedience, the inputLabel is the level (e.g., "Level 1") but show_element is "C-WAGS Obedience"
    // So we need to check by level only, similar to Barn Hunt
    const isCWagsObedience = props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                             props.show.show_type === Constants.show_type.obedience;

    // For C-WAGS Rally, the inputLabel is the level (e.g., "Starter") but show_element is "C-WAGS Rally"
    // So we need to check by level only, similar to Barn Hunt
    const isCWagsRally = props.event?.sanctioning_club === Constants.sanctioning_club.C_WAGS &&
                         props.show.show_type === Constants.show_type.rally_obedience;

    // For SDG with allowDuplicates, rows share the same show_element+level but have different IDs.
    // Match by inputId so each row independently tracks its own class.
    const isSDGWithDuplicates = props.event?.sanctioning_club === Constants.sanctioning_club.ABI &&
                                props.show.show_type === Constants.show_type.scent_dog_games &&
                                inputId !== undefined;
    if (isSDGWithDuplicates) {
      return classes.filter((c: IDogClassElement) => c.id === inputId && c.level === optionId).length > 0;
    }

    return isSpecialClassesSection || isBarnHunt || isCWagsObedience || isCWagsRally
      ? classes.filter((c: IDogClassElement) => c.level === optionId).length > 0
      : classes.filter((c: IDogClassElement) => (inputLabel === c.show_element && c.level === optionId)).length > 0;
  }

  return(
    <div className="FormBuilder">
      <div>
        <div className="form">
          {/* Render timeslots BEFORE form rows for FastCAT/FastDash/CAT (timeslot IS the form for these).
              For Fetch, Obedience Show N Go, and Rally Show N Go, timeslots render AFTER form rows so the user picks a class level first. */}
          {helperService.timeSlotShows.includes(props.show.show_type) &&
           props.show.show_type !== Constants.show_type.fetch &&
           props.show.show_type !== Constants.show_type.obedience_show_n_go &&
           props.show.show_type !== Constants.show_type.general_rally && <div className="timeSlot-wrap">
            {timeslots.map((t: IShowRunTimeSlot, tIndex: number) => {
              const isFull = (t.num_available - t.num_reserved <= 0);
              const isWaitlistSlot = isFull && props.show.waitlist_enabled;

              // Determine if this specific timeslot is currently selected (active)
              const isSlotSelected = (props.show.show_type === Constants.show_type.fast_cat ||
                                      props.show.show_type === Constants.show_type.fast_dash ||
                                      props.show.show_type === Constants.show_type.cat)
                ? classes.some(c => c.time_slot === t.time_slot)
                : selectedTimeslot === tIndex;

              // Estimate waitlist position: current count + 1 (shown only when selected)
              const estimatedWaitlistPosition = isWaitlistSlot && isSlotSelected
                ? ((props.show as any).waitlist_count ?? 0) + 1
                : undefined;

              return(
                <TimeSlot
                  className={getTimeSlotClass(t, tIndex)}
                  timeslot={t}
                  key={tIndex}
                  waitlistEnabled={props.show.waitlist_enabled}
                  estimatedWaitlistPosition={estimatedWaitlistPosition}
                  onClick={(e) => {
                    if (!isTimeSlotDisabled(t)) {
                      // For Fast Cat, Fast Dash & CAT, check if this timeslot is already in classes
                      // For other show types, use selectedTimeslot index
                      const isCurrentlySelected = (props.show.show_type === Constants.show_type.fast_cat || 
                                                    props.show.show_type === Constants.show_type.fast_dash ||
                                                    props.show.show_type === Constants.show_type.cat)
                        ? classes.some(c => c.time_slot === t.time_slot)
                        : selectedTimeslot === tIndex;
                      
                      if (isCurrentlySelected) {
                        // Deselect: clear the selection and remove the class
                        if (props.show.show_type !== Constants.show_type.fast_cat && 
                            props.show.show_type !== Constants.show_type.fast_dash &&
                            props.show.show_type !== Constants.show_type.cat) {
                          setSelectedTimeslot(-1);
                        }
                        updateClasses({
                          id: props.show.show_type,
                          type: "timeslot",
                          label: props.show.show_type,
                          options: []
                        }, { id: t._id, value: t.time_slot}, undefined, undefined, undefined, true);
                      } else {
                        // Select: set new selection and add/update the class
                        if (props.show.show_type !== Constants.show_type.fast_cat && 
                            props.show.show_type !== Constants.show_type.fast_dash &&
                            props.show.show_type !== Constants.show_type.cat) {
                          setSelectedTimeslot(tIndex);
                        }
                        updateClasses({
                          id: props.show.show_type,
                          type: "timeslot",
                          label: props.show.show_type,
                          options: []
                        }, { id: t._id, value: t.time_slot});
                      }
                    }
                  }}
                />
              );
            })}
          </div>}
          {form !== undefined && form.map((row: IEventRegisterRowElement, rIndex: number) => {
            const isFeoRow = row.inputs.some((i) => i.section === Constants.dog_class_section.feo);
            const rowClass = [
              "row",
              row.inputs.length <= 1 ? "single" : "",
              row.duplicate ? "duplicate" : "",
              isFeoRow ? "feo" : "",
            ].filter(Boolean).join(" ");
            return(
              <div className={rowClass} key={rIndex}>
                <div className="rowInputs">
                {row.inputs.length === 0 ? (
                  <div className="noAdditionalInfo">
                    <p>No additional class information is required for this event.</p>
                  </div>
                ) : (
                  row.inputs.map((input: IEventRegisterInputElement, iIndex: number) => {
                  return(
                    <div key={iIndex} style={{ flex: input.flex ? input.flex : 1 }} className="inputWrap">
                      <div className="input">
                        {input.type === "dropdown" && !(input.label === "Section" && iIndex > 0 && !isElementAndAvailable(row.inputs[iIndex - 1])) && <Dropdown
                            id={input.id + "-" + input.elementType}
                            placeholder=""
                            value={getValue(input)}
                            onChange={(e: any, value: IOption) => { if (e.target.value.length > 0) { updateClasses(input, value); } }}
                            label={getLabel(input)}
                            alignRight={input.label === Constants.dog_class_element.conformation_altered}
                            options={ageRestrictedElements.includes(input.label) ? ageRestrictOptions(input.options) : (row.allowDuplicates || row.duplicate) ? dupCheckOptions(input) : filterDropdownOptions(input)}
                            dropUp={input.dropUp ? input.dropUp : false}
                            disabled={isDropdownDisabled(input)}
                          />
                        }
                        {input.type === "text" && (
                          <Input
                            className={
                              props.event?.sanctioning_club === Constants.sanctioning_club.ASFA &&
                              props.show.show_type === Constants.show_type.field_trial &&
                              input.id === "asfa_info_changed_regarding"
                                ? "asfaRegardingSpacing"
                                : ""
                            }
                            id={input.id}
                            type="text"
                            label={getLabel(input)}
                            defaultValue={getValue(input).value}
                            onChange={(e: any) => {
                              updateClasses(input, { id: input.id || "", value: e.target.value });
                            }}
                          />
                        )}
                        {(input.type === "checkbox") && isElementAndAvailable(input) && !props.moveUp && <div className="checkboxWrap">
                          {input.section !== Constants.dog_class_section.feo &&
                            <div className="inputTitle">{getCheckboxTitle(input)}</div>
                          }
                          <ul className={`checkboxList${input.columnLayout ? " columns" : ""}`}>
                            {input.options.map((option:IOption, cIndex: number) => {
                              const isSpecialClassesSection = input.label === Constants.dog_class_element.conformation_special_classes;
                              const isChecked = getIsChecked(isSpecialClassesSection, option.id, input.label, input.id, input.section)
                              const isAsfaQuestionCheckbox = isAsfaMetadataInput(input.id);
                              
                              // Check if this class is waitlisted using the waitlistedClasses prop
                              const isWaitlisted = props.waitlistedClasses?.some((wc) => {
                                // For BHA Barn Hunt: input.label IS the level (e.g. "Novice"),
                                // and wc.show_element is "Barn Hunt". Match on level instead.
                                if (props.show.show_type === Constants.show_type.barn_hunt) {
                                  return (
                                    wc.show_element === Constants.dog_class_element.barn_hunt &&
                                    (wc.level === input.label || wc.level?.startsWith(input.label + " "))
                                  );
                                }
                                return wc.show_element === input.label && wc.level === option.id && sameSectionAsInput(wc, input);
                              }) || false;
                              
                              // Create unique ID - use different logic for edit mode (Change Elements) vs normal registration
                              // In edit mode, we need show ID + row-input-checkbox indices to avoid conflicts between multiple show sections (e.g., Trial 1 vs Trial 2)
                              // In normal registration, we need show ID + dog ID + input ID + checkbox index to ensure each show's checkboxes work independently
                              // This fixes the bug where registering multiple dogs to the same show/trial causes checkboxes to toggle incorrectly
                              // AND ensures that when "use same for all" is unchecked, each show's checkboxes are independent
                              const dogId = '_id' in props.dog ? props.dog._id : props.dog.dog_id;
                              const uniqueId = props.edit 
                                ? `${props.show._id}-${rIndex}-${iIndex}-${cIndex}` 
                                : `${props.show._id}-${dogId}-${input.id}-${cIndex}`;

                              if (isAsfaQuestionCheckbox || !isCheckboxDisabled(option.id, input.label)) {
                                return(<li key={uniqueId}><Checkbox className={isWaitlisted ? "waitlisted" : ""} onChange={(e:any) => { updateClasses(input, option); }} value={isChecked} id={uniqueId} label={option.value} /></li>);
                              } else {
                                return(<li key={uniqueId}><Checkbox className={isWaitlisted ? "waitlisted" : ""} disabled={true} onChange={(e:any) => { /* Do Nothing */ }} value={isChecked} id={uniqueId} label={option.value} /></li>);
                              }
                            })}
                          </ul>
                          {/* {classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.dock_jumping_air).length > 0 &&
                            <div className="otherWrap">
                              <Input required label={"Number of Splashes"} id={input.id+"-multiplier"} type="number" onChange={(e:any) => { e.stopPropagation(); updateClasses(input, { id: "multiplier", value: e.target.value }, undefined, undefined, e.target.value); }} defaultValue={classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.dock_jumping_air)[0].multiplier?.toString() || "0"} />
                            </div>
                          } */}
                          {classes.filter((c: IDogClassElement) => c.level === "Other" && (input.label === c.show_element || input.label === c.level)).length > 0 &&
                            <div className="otherWrap">
                              <Input required label="Other" id={input.id+"-other"} type="text" onChange={(e:any) => { e.stopPropagation(); updateClasses(input, { id: "Other", value: e.target.value }, e.target.value); }} />
                            </div>
                          }
                          {classes.find((c: IDogClassElement) => 
                            c.level === Constants.dog_class_element_level.brace && 
                            // Exclude AKC Conformation Special Classes - they have show_element "Conformation"
                            !(c.show_element === Constants.dog_class_element.conformation && props.event?.sanctioning_club === Constants.sanctioning_club.AKC && props.show.show_type === Constants.show_type.conformation) &&
                            (input.label === c.show_element || input.label === c.level)
                          ) &&
                            <div className="otherWrap">
                              {props.userDogs && <DogList
                                label={`${getDogListLabel(Constants.dog_class_element_level.brace)}' Teammates`}
                                club={props.event?.sanctioning_club}
                                show={props.showDogs}
                                dogs={props.userDogs}
                                selectedDogs={getBraceOrPairDogs(Constants.dog_class_element_level.brace)}
                                hasAvailableSpot={!getBraceOrPairDogs(Constants.dog_class_element_level.brace).length}
                                onDogClick={(dog) => { handleBraceDogSelect(dog, input); }}
                                onAddDog={props.onAddDog!}
                                onEditDog={props.onEditDog!}
                              />}
                            </div>
                          }
                          {classes.find((c: IDogClassElement) => c.level === Constants.dog_class_element_level.pairs && (input.label === c.show_element || input.label === c.level)) &&
                            <div className="otherWrap">
                              {props.userDogs && <DogList
                                label={`${getDogListLabel(Constants.dog_class_element_level.pairs)}' Teammates`}
                                club={props.event?.sanctioning_club}
                                show={props.showDogs}
                                dogs={props.userDogs}
                                selectedDogs={getBraceOrPairDogs(Constants.dog_class_element_level.pairs)}
                                hasAvailableSpot={!getBraceOrPairDogs(Constants.dog_class_element_level.pairs).length}
                                onDogClick={(dog) => { handlePairsDogSelect(dog, input); }}
                                onAddDog={props.onAddDog!}
                                onEditDog={props.onEditDog!}
                              />}
                            </div>
                          }
                          {classes.find((c: IDogClassElement) => c.level === Constants.dog_class_element_level.novice_pairs && (input.label === c.show_element || input.label === c.level)) &&
                            <div className="otherWrap">
                              {props.userDogs && <DogList
                                label={`${getDogListLabel(Constants.dog_class_element_level.novice_pairs)}' Teammates`}
                                club={props.event?.sanctioning_club}
                                show={props.showDogs}
                                dogs={props.userDogs}
                                selectedDogs={getBraceOrPairDogs(Constants.dog_class_element_level.novice_pairs)}
                                hasAvailableSpot={!getBraceOrPairDogs(Constants.dog_class_element_level.novice_pairs).length}
                                onDogClick={(dog) => { handleNovicePairsDogSelect(dog, input); }}
                                onAddDog={props.onAddDog!}
                                onEditDog={props.onEditDog!}
                              />}
                            </div>
                          }
                          {classes.find((c: IDogClassElement) => c.level === Constants.dog_class_element_level.advanced_pairs && (input.label === c.show_element || input.label === c.level)) &&
                            <div className="otherWrap">
                              {props.userDogs && <DogList
                                label={`${getDogListLabel(Constants.dog_class_element_level.advanced_pairs)}' Teammates`}
                                club={props.event?.sanctioning_club}
                                show={props.showDogs}
                                dogs={props.userDogs}
                                selectedDogs={getBraceOrPairDogs(Constants.dog_class_element_level.advanced_pairs)}
                                hasAvailableSpot={!getBraceOrPairDogs(Constants.dog_class_element_level.advanced_pairs).length}
                                onDogClick={(dog) => { handleAdvancedPairsDogSelect(dog, input); }}
                                onAddDog={props.onAddDog!}
                                onEditDog={props.onEditDog!}
                              />}
                            </div>
                          }
                          {classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.shed_dog_youth && (input.label === c.show_element || input.label === c.level)).length > 0 &&
                            <div className="otherWrap">
                              <Input required label="Youth Name" id={input.id+"-extra"} type="text" onChange={(e:any) => { e.stopPropagation(); updateClasses(input, { id: "extra", value: e.target.value }, e.target.value, undefined, Constants.dog_class_element_level.shed_dog_youth); }} defaultValue={classes.find((c: IDogClassElement) => c.level === Constants.dog_class_element_level.shed_dog_youth)?.extra} />
                            </div>
                          }
                          {/* C-WAGS Games — Pairs / Team partner. Each dog is its own entry (C-WAGS
                              requires an entry form per dog); the partner is recorded as free text on
                              every selected class of that game (dog_class.extra). Pairs must name the
                              other dog; Team may leave it blank to be paired at the trial. */}
                          {isCWagsGamesPartnerElement(input.label) &&
                            !input.section && /* one partner input per game; it also covers the FEO row (same show_element) */
                            classes.some((c: IDogClassElement) => c.show_element === input.label) &&
                            <div className="otherWrap">
                              <Input
                                required={input.label === Constants.dog_class_element.cwags_games_pairs}
                                label={input.label === Constants.dog_class_element.cwags_games_pairs
                                  ? "Partner Dog (name / registration #)"
                                  : "Team Partner (handler & dog) — leave blank to be paired at the trial"}
                                id={input.id + "-extra"}
                                type="text"
                                onChange={(e: any) => { e.stopPropagation(); updateCWagsGamesPartner(input.label, e.target.value); }}
                                defaultValue={classes.find((c: IDogClassElement) => c.show_element === input.label)?.extra ?? ""}
                              />
                            </div>
                          }
                          {classes.filter((c: IDogClassElement) => c.level === Constants.dog_class_element_level.team && (input.label === c.show_element || input.label === c.level)).length > 0 && <>
                            <div className="otherWrap team">
                              {props.userDogs && <DogList
                                label="Team' Teammates"
                                club={props.event?.sanctioning_club}
                                show={props.showDogs}
                                dogs={props.userDogs}
                                selectedDogs={getTeamDogs()}
                                hasAvailableSpot={getTeamDogs().length < 3}
                                onDogClick={(dog) => { handleTeamDogSelect(dog, input); }}
                                onAddDog={props.onAddDog!}
                                onEditDog={props.onEditDog!}
                              />}
                            </div>
                          </>}
                          {/* Extra teammates # input group */}
                          {props.event?.sanctioning_club === Constants.sanctioning_club.ABI && <>
                            {!!classes.find((c: IDogClassElement) => {
                              return c.level === Constants.dog_class_element_level.abi_stud_dog
                                && (input.label === c.show_element || input.label === c.level)
                            }) &&
                              <ExtraTeammates
                                level={Constants.dog_class_element_level.abi_stud_dog}
                                input={input}
                                dogClass={classes.find((c) => c.level === (Constants.dog_class_element_level.abi_stud_dog))}
                                onChange={updateClasses}
                                label="Stud Dog' teammates"
                                className="otherWrap team"
                              />
                            }
                            {!!classes.find((c: IDogClassElement) => {
                              return c.level === Constants.dog_class_element_level.abi_brood_bitch
                                && (input.label === c.show_element || input.label === c.level)
                            }) &&
                              <ExtraTeammates
                                level={Constants.dog_class_element_level.abi_brood_bitch}
                                input={input}
                                dogClass={classes.find((c) => c.level === (Constants.dog_class_element_level.abi_brood_bitch))}
                                onChange={updateClasses}
                                label="Brood Bitch' teammates"
                                className="otherWrap team"
                              />
                            }
                          </>}
                          {props.event?.sanctioning_club === Constants.sanctioning_club.ISWS && <>
                            {!!classes.find((c: IDogClassElement) => {
                              return c.level === Constants.dog_class_element_level.isws_stud_dog
                                && (input.label === c.show_element || input.label === c.level)
                            }) &&
                              <ExtraTeammates
                                level={Constants.dog_class_element_level.isws_stud_dog}
                                input={input}
                                dogClass={classes.find((c) => c.level === (Constants.dog_class_element_level.isws_stud_dog))}
                                onChange={updateClasses}
                                label="Stud Dog' teammates"
                                className="otherWrap team"
                              />
                            }
                            {!!classes.find((c: IDogClassElement) => {
                              return c.level === Constants.dog_class_element_level.isws_brood_bitch
                                && (input.label === c.show_element || input.label === c.level)
                            }) &&
                              <ExtraTeammates
                                level={Constants.dog_class_element_level.isws_brood_bitch}
                                input={input}
                                dogClass={classes.find((c) => c.level === (Constants.dog_class_element_level.isws_brood_bitch))}
                                onChange={updateClasses}
                                label="Brood Bitch' teammates"
                                className="otherWrap team"
                              />
                            }
                          </>}
                          {/* AKC Extra dogs selection */}
                          {props.event?.sanctioning_club === Constants.sanctioning_club.AKC && <>
                            {!!classes.find((c: IDogClassElement) => {
                              return c.level === Constants.dog_class_element_level.akc_brace
                                && input.label === Constants.dog_class_element.conformation_special_classes
                            }) &&
                              <div className="otherWrap">
                                {props.userDogs && <DogList
                                  label="Brace Partner"
                                  club={props.event?.sanctioning_club}
                                  show={props.showDogs}
                                  dogs={props.userDogs}
                                  selectedDogs={getAKCBraceDogs()}
                                  hasAvailableSpot={true}
                                  onDogClick={(dog) => { handleAKCBraceSelect(dog, input); }}
                                  onAddDog={props.onAddDog!}
                                  onEditDog={props.onEditDog!}
                                />}
                              </div>
                            }
                          </>}
                          {/* Multi-class messages for AKC/UKC multi-entries */}
                          {input.multiClassMessages && Object.keys(input.multiClassMessages).map(multiClassLevel => {
                            const selectedClass = classes.find((c: IDogClassElement) => 
                              c.show_element === input.label && c.level === multiClassLevel
                            );
                            if (selectedClass) {
                              return (
                                <div key={multiClassLevel} className="multiClassMessage">
                                  By selecting {multiClassLevel}, {props.dog.call_name || props.dog.registered_name} will be entered in: {input.multiClassMessages![multiClassLevel]}
                                </div>
                              );
                            }
                            return null;
                          })}
                          
                          {/* Multi-level warning for Trick Dog */}
                          {input.multiLevelWarning && (() => {
                            const selectedLevels = classes.filter((c: IDogClassElement) => 
                              c.show_element === input.label && c.level && c.level !== "None"
                            );
                            if (selectedLevels.length > 1) {
                              return (
                                <div className="multiClassMessage">
                                  {input.multiLevelWarning}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>}
                      </div>
                    </div>
                  );
                }))}
                </div>
                {row.allowDuplicates && !props.edit && canAddMoreLevels(row) && <div className="rowActions"><div className="duplicateBtn" onClick={(e) => { duplicateRow(rIndex); }}><span className="icon"></span></div></div>}
                {row.duplicate && !props.edit && <div className="rowActions"><div className="removeBtn" onClick={(e) => { removeRow(rIndex); }}><span className="icon"></span></div></div>}
              </div>
            );
          })}
          {/* Render timeslots AFTER form rows for Fetch, Obedience Show N Go, and Rally Show N Go so the user picks a level first */}
          {(props.show.show_type === Constants.show_type.fetch ||
            props.show.show_type === Constants.show_type.obedience_show_n_go ||
            props.show.show_type === Constants.show_type.general_rally) &&
           timeslots.length > 0 && (() => {
            // Per-class mode: slots have level set and show type is OSNG/Rally
            const isPerClassMode =
              (props.show.show_type === Constants.show_type.obedience_show_n_go ||
               props.show.show_type === Constants.show_type.general_rally) &&
              timeslots.some((t) => !!t.level);

            if (isPerClassMode) {
              // Group slots by level (the shared pool a given level draws from)
              const slotsByLevel: Record<string, IShowRunTimeSlot[]> = {};
              for (const t of timeslots) {
                const lvl = t.level ?? "";
                if (!slotsByLevel[lvl]) { slotsByLevel[lvl] = []; }
                slotsByLevel[lvl].push(t);
              }

              // Render one picker section PER SELECTED CLASS ENTRY (not per unique level) so
              // duplicate entries of the same level (added via the "+" button) each get their
              // own independent timeslot selection, keyed by that entry's unique row id.
              const selectedEntries = classes.filter(
                (c) => c.level && c.level !== "None" && slotsByLevel[c.level]
              );
              const occurrenceByLevel: Record<string, number> = {};

              return (
                <div className="perClassTimeslotPickers">
                  {selectedEntries.map((cls) => {
                    const lvl = cls.level as string;
                    const levelSlots = slotsByLevel[lvl];
                    const instanceId = cls.id;
                    occurrenceByLevel[lvl] = (occurrenceByLevel[lvl] || 0) + 1;
                    const occurrence = occurrenceByLevel[lvl];

                    return (
                      <div key={instanceId} className="perClassTimeslotSection">
                        <div className="perClassTimeslotLabel">
                          Select a time for <strong>{lvl}</strong>{occurrence > 1 ? ` (Entry ${occurrence})` : ""}:
                        </div>
                        <div className="timeSlot-wrap">
                          {levelSlots.map((t, tIndex) => {
                            const isFull = (t.num_available - t.num_reserved <= 0);
                            const isWaitlistSlot = isFull && props.show.waitlist_enabled;
                            const isSlotSelected = selectedTimeslotByLevel[instanceId] === tIndex;
                            const estimatedWaitlistPosition = isWaitlistSlot && isSlotSelected
                              ? ((props.show as any).waitlist_count ?? 0) + 1
                              : undefined;
                            return (
                              <TimeSlot
                                key={tIndex}
                                className={getTimeSlotClass(t, tIndex, instanceId)}
                                timeslot={t}
                                waitlistEnabled={props.show.waitlist_enabled}
                                estimatedWaitlistPosition={estimatedWaitlistPosition}
                                onClick={() => {
                                  if (!isTimeSlotDisabled(t, instanceId)) {
                                    if (isSlotSelected) {
                                      // Deselect
                                      setSelectedTimeslotByLevel((prev) => {
                                        const next = { ...prev };
                                        delete next[instanceId];
                                        return next;
                                      });
                                      setClasses((prev) =>
                                        prev.map((c) =>
                                          c.id === instanceId ? { ...c, time_slot: undefined } : c
                                        )
                                      );
                                    } else {
                                      // Select
                                      setSelectedTimeslotByLevel((prev) => ({ ...prev, [instanceId]: tIndex }));
                                      setClasses((prev) =>
                                        prev.map((c) =>
                                          c.id === instanceId ? { ...c, time_slot: t.time_slot } : c
                                        )
                                      );
                                    }
                                  }
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }

            // Legacy flat timeslot list (Fetch, or OSNG/Rally without per-level slots)
            return (
              <div className="timeSlot-wrap">
                {timeslots.map((t: IShowRunTimeSlot, tIndex: number) => {
                  const isFull = (t.num_available - t.num_reserved <= 0);
                  const isWaitlistSlot = isFull && props.show.waitlist_enabled;
                  const isSlotSelected = selectedTimeslot === tIndex;
                  const estimatedWaitlistPosition = isWaitlistSlot && isSlotSelected
                    ? ((props.show as any).waitlist_count ?? 0) + 1
                    : undefined;
                  return (
                    <TimeSlot
                      className={getTimeSlotClass(t, tIndex)}
                      timeslot={t}
                      key={tIndex}
                      waitlistEnabled={props.show.waitlist_enabled}
                      estimatedWaitlistPosition={estimatedWaitlistPosition}
                      onClick={(e) => {
                        if (!isTimeSlotDisabled(t)) {
                          const isCurrentlySelected = selectedTimeslot === tIndex;
                          if (isCurrentlySelected) {
                            setSelectedTimeslot(-1);
                            updateClasses({
                              id: props.show.show_type,
                              type: "timeslot",
                              label: props.show.show_type,
                              options: []
                            }, { id: t._id, value: t.time_slot}, undefined, undefined, undefined, true);
                          } else {
                            setSelectedTimeslot(tIndex);
                            updateClasses({
                              id: props.show.show_type,
                              type: "timeslot",
                              label: props.show.show_type,
                              options: []
                            }, { id: t._id, value: t.time_slot});
                          }
                        }
                      }}
                    />
                  );
                })}
              </div>
            );
          })()}
        </div>
      </div>
      {missingFieldsRequest && (
        <MissingFieldsModal
          context={missingFieldsRequest.context}
          fields={missingFieldsRequest.fields}
          levels={missingFieldsRequest.context.level ? [missingFieldsRequest.context.level] : undefined}
          isAdmin={props.isClubManager}
          eventId={props.event?._id}
          onSaved={(updated) => {
            dogRef.current = updated;
            const retry = pendingClassRetryRef.current;
            pendingClassRetryRef.current = null;
            setMissingFieldsRequest(null);
            props.onRefreshDogData?.(updated);
            retry?.();
          }}
          onProgress={(updated) => {
            dogRef.current = updated;
            props.onRefreshDogData?.(updated);
          }}
          onCancel={() => {
            pendingClassRetryRef.current = null;
            setMissingFieldsRequest(null);
          }}
        />
      )}
    </div>
  );
}

export default FormBuilder;